import { useState, useEffect, useRef } from "react";
import { getCurrentUser, getMyProfile, supabase, createListing, uploadPhoto, signOut, createSellerProfile, updateProfile, sendMessage } from "../lib/supabase";
import { MapContainer, TileLayer, Circle, useMap } from "react-leaflet";

function MapFit({ center, radiusMi }) {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    const L = window.L || require("leaflet");
    const radiusM = radiusMi * 1609.34;
    const bounds = L.latLng(center).toBounds(radiusM * 2);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, center, radiusMi]);
  return null;
}

/* ─── TOKENS ─────────────────────────────── */
const D = {
  black:"#0A0A0A", white:"#FFFFFF", red:"#FF0000",
  gray50:"#F7F7F7", gray100:"#EEEEEE", gray200:"#DDDDDD",
  gray400:"#AAAAAA", gray600:"#666666", gray800:"#333333",
  green:"#00B341", orange:"#FF6B00",
  radius:6,   // card/button corner radius — slightly soft but not bubbly
  radiusSm:4, // small elements
};

/* ─── DATA ───────────────────────────────── */
// ─── BANNERS ──────────────────────────────────────────────────────────────────
// Add/remove/edit banners here. Each banner: { bg, color, label, title, sub, cta, ctaUrl, img }
// img: URL string (optional). cta/ctaUrl: button text + link (optional).
const BANNERS = [
  {
    bg: "#0A0A0A",
    color: "#FFFFFF",
    label: "NEW",
    title: "Discover local talent",
    sub: "Find services and goods from people in your area.",
    cta: "Browse all",
    ctaUrl: null, // set to a URL string to open a link, or null to switch to Browse all tab
  },
  {
    bg: "#FF0000",
    color: "#FFFFFF",
    label: "FEATURED",
    title: "List your service today",
    sub: "Join nearme. and start earning from your skills.",
    cta: "Get started",
    ctaUrl: null,
  },
];
// ──────────────────────────────────────────────────────────────────────────────

const CORE_CATS = ["Hair & Beauty","Art","Food","Fitness","Fashion","Photography","Wellness","Tech","Tutoring","Events","Trades","Other"];
const CAT_ICONS = {"Hair & Beauty":"💇","Art":"🎨","Food":"🍔","Fitness":"💪","Fashion":"👗","Photography":"📷","Wellness":"🧘","Tech":"💻","Tutoring":"📚","Events":"🎉","Trades":"🔧","Other":"🏷️"};
const CAT_BG    = ["#FFF0F0","#F0F0FF","#FFF8EE","#F0F8FF","#FFF0F8","#F8F0FF","#F0FFFA","#F5F0FF","#F0FFF8","#FFF5EE","#F2F2F2","#F5F5F5"];
const SORT_OPTS = ["Quickest","Closest","Price ↑","Top Rated","Retention"];

const CATEGORY_DISCLOSURES = {
  "Hair & Beauty": {
    title: "Hair & Beauty — PA Cosmetology License",
    intro: "Pennsylvania requires a State Board license for cosmetology services. Please confirm:",
    checks: [
      { id: "beauty", label: "PA cosmetology, nail, esthetics, and barbering services require a State Board of Cosmetology license. I will only perform services I am properly licensed for." },
    ],
  },
  "Food": {
    title: "Food — PA Food Safety",
    intro: "Before listing food services or products, please confirm:",
    checks: [
      { id: "food-svc", label: "I comply with all applicable Pennsylvania food safety regulations, including the PA Cottage Food Act for shelf-stable homemade goods and any required licensing for prepared food services." },
    ],
  },
};

const discKey  = (uid, cat) => `nm_d_${uid}_${cat.toLowerCase().replace(/[ &]/g, '_')}`;
const hasDisc  = (uid, cat) => !!(uid && localStorage.getItem(discKey(uid, cat)));
const saveDisc = (uid, cat) => uid && localStorage.setItem(discKey(uid, cat), '1');


const ME_DEFAULT = {
  name:"You", handle:"@me", avatar:"ME", avatarUrl:null,
  bio:"",
  following:[],
  topChoices:[],
  visitLog:[],
  interests:[],
};

const slotColor  = m => m===0?D.green:m<=60?D.orange:m<=240?"#CC5500":D.gray400;
const slotLabel  = m => m===0?"Now":m<60?`${m}m`:m<1440?`${Math.floor(m/60)}hr`:"Tomorrow";
const isProd     = l => l.kind==="product";

/* ─── MINI HELPERS ───────────────────────── */
const Label = ({children, style={}}) => (
  <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:D.gray600,...style}}>{children}</div>
);
const Avatar = ({url, initials, size, radius}) => (
  url
    ? <img src={url} alt="" style={{width:size,height:size,borderRadius:radius??D.radius,objectFit:"cover",display:"block",flexShrink:0}}/>
    : <div style={{width:size,height:size,background:D.black,borderRadius:radius??D.radius,display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.round(size*0.32),fontWeight:900,color:"#fff",flexShrink:0}}>{initials}</div>
);
const Chip = ({active, onClick, children}) => (
  <button onClick={onClick} style={{padding:"5px 12px",border:`1.5px solid ${active?D.black:D.gray200}`,borderRadius:D.radius,background:active?D.black:D.white,color:active?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"Noto Sans,sans-serif",letterSpacing:"0.02em"}}>
    {children}
  </button>
);

/* ─── APP ────────────────────────────────── */
export default function App({ profile, onSignOut, guest, onSignIn, initialHandle, initialListingId }) {
  const [tab, setTab]               = useState("explore");
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const [exploreKind, setExploreKind] = useState("both"); // "services" | "goods" | "both"
  const [feedTab, setFeedTab]       = useState("following");
  const [subCat, setSubCat]         = useState(new Set());
  const [sort, setSort]             = useState("Quickest");
  const [selected, setSelected]     = useState(null);
  const openListing = (l) => { setSelected(l); const lid = String(l?.lid||''); if (lid && !lid.endsWith('-feed') && !/-svc-\d+$/.test(lid)) window.history.pushState(null,'',`/listing/${lid}`); };
  const closeListing = () => { setSelected(null); window.history.pushState(null,'',sellerView ? '/'+String(sellerView.handle||'').replace(/^@/,'') : '/'); };
  const [sellerView, setSellerView] = useState(null);
  const openSeller = (seller, tab="posts") => { setSellerView(seller); setProfileTab(tab); if (seller?.handle) window.history.pushState(null,'','/'+seller.handle.replace(/^@/,'')); };
  const closeSeller = () => { setSellerView(null); window.history.pushState(null,'','/'); };
  const [profileTab, setProfileTab] = useState("posts");
  const [userProfile, setUserProfile] = useState(false);
  const [following, setFollowing]   = useState(new Set());
  const [saved, setSaved]           = useState(new Set());
  const [flashOnly, setFlashOnly]   = useState(false);
  const [goodsFulfill, setGoodsFulfill] = useState("pickup"); // pickup | delivery | mail
  const [radiusFilter, setRadiusFilter] = useState(1);
  const [campusMode, setCampusMode]     = useState(false);
  const [cityLabel, setCityLabel]       = useState("Nearby");
  const [userCoords, setUserCoords]     = useState(null);
  const [showRadiusModal, setShowRadiusModal] = useState(false);
  const [sliderRadius, setSliderRadius]       = useState(1);
  const [me, setMe] = useState(() => {
    if (!profile) return ME_DEFAULT;
    const initials = profile.name
      ? profile.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()
      : "ME";
    return {
      name: profile.name || "You",
      handle: profile.handle || "@me",
      avatar: initials,
      avatarUrl: profile.avatar_url || null,
      bio: profile.bio || "",
      locationText: profile.location_text || "",
      showLocation: profile.show_location ?? false,
      following: [],
      topChoices: [],
      visitLog: [],
      interests: profile.interests || [],
    };
  });

  // search
  const [searchQuery, setSearchQuery]   = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [searchTab, setSearchTab]       = useState("categories");
  const [searchSort, setSearchSort]     = useState("relevance");
  const [dbUserResults, setDbUserResults] = useState([]);

  // real listings from db
  const [dbListings, setDbListings] = useState([]);
  const [dbLoading, setDbLoading]   = useState(true);
  const [dbVersion, setDbVersion]   = useState(0); // increment to re-fetch

  // my own listings for profile store tab
  const [myListings, setMyListings] = useState(null); // null=not loaded
  const [myProfileTab, setMyProfileTab] = useState("profile");
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // desktop filter dropdown
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  // desktop landing tabs: "category" | "all"
  const [desktopLandingTab, setDesktopLandingTab] = useState(() => window.innerWidth >= 768 ? "all" : "category");

  // first-visit welcome
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('nearme_welcomed'));
  const dismissWelcome = () => { localStorage.setItem('nearme_welcomed', '1'); setShowWelcome(false); };

  // guest auth gate
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const requireAuth = (fn) => { if (guest) { setShowAuthPrompt(true); } else { fn(); } };

  // message compose
  const [showMsgCompose, setShowMsgCompose] = useState(false);
  const [msgText, setMsgText]               = useState("");
  const [msgSending, setMsgSending]         = useState(false);
  const [msgSent, setMsgSent]               = useState(false);

  // notifications
  const [showNotifs, setShowNotifs]     = useState(false);
  const [notifs, setNotifs]             = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [notifsTab, setNotifsTab]       = useState("notifications");
  const [hasUnread, setHasUnread]       = useState(false);

  // listings for seller profile view
  const [sellerListings, setSellerListings] = useState([]);
  const [sellerListingsLoading, setSellerListingsLoading] = useState(false);

  // sell flow
  const [sellStep, setSellStep]         = useState("type"); // "type"|"form"|"disclosure"|"done"
  const [sellKind, setSellKind]         = useState(null);   // "service"|"goods"
  const [mySellerProfile, setMySellerProfile] = useState(null); // null=unchecked, false=none, obj=exists
  const [sellUserId, setSellUserId]         = useState(null);
  const [goodsAgreed, setGoodsAgreed]       = useState(false);
  const [servicesAgreed, setServicesAgreed] = useState(false);
  const [agreeLoading, setAgreeLoading]     = useState(null); // "goods"|"services"|null
  const [showTermsFor, setShowTermsFor]     = useState(null); // null|"goods"|"services"
  const [pendingDisclosure, setPendingDisclosure]     = useState(null);
  const [disclosureChecked, setDisclosureChecked]     = useState(new Set());
  const [listingName, setListingName]   = useState("");
  const [listingDesc, setListingDesc]   = useState("");
  const [listingPrice, setListingPrice] = useState("");
  const [listingCat, setListingCat]     = useState("");
  const [listingSlot, setListingSlot]   = useState(0);
  const [listingFlash, setListingFlash] = useState(false);
  const [listingMsgToBook, setListingMsgToBook] = useState(false);
  const [listingPhotoFiles, setListingPhotoFiles] = useState([]);
  const [listingPhotoURLs, setListingPhotoURLs]   = useState([]);
  const [sellError, setSellError]       = useState("");
  const [sellLoading, setSellLoading]   = useState(false);
  const photoInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadPhoto(file, "avatars");
      setMe(m => ({ ...m, avatarUrl: url }));
      await updateProfile({
        name: me.name,
        handle: me.handle,
        bio: me.bio,
        interests: me.interests,
        avatar_url: url,
      });
    } catch (err) {
      console.error("Avatar upload failed:", err);
    }
  };

  const loadNotifs = async () => {
    setNotifsLoading(true);
    setNotifs([]);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const uid = authUser.id;
      const items = [];

      // Messages sent to me
      const { data: msgs } = await supabase
        .from("messages")
        .select("id, body, created_at, sender:users!sender_id(name, handle, avatar_url)")
        .eq("receiver_id", uid)
        .order("created_at", { ascending: false })
        .limit(30);
      (msgs || []).forEach(m => items.push({
        id: `msg-${m.id}`, type: "message",
        text: `${m.sender?.handle || "Someone"} sent you a message`,
        sub: m.body,
        at: m.created_at,
        avatarUrl: m.sender?.avatar_url || null,
        initials: m.sender?.name ? m.sender.name[0].toUpperCase() : "?",
      }));

      // Seller-side notifications
      const { data: sellerRow } = await supabase
        .from("sellers").select("id").eq("user_id", uid).maybeSingle();

      if (sellerRow) {
        const { data: bks } = await supabase
          .from("bookings")
          .select("id, created_at, status, listings(name), buyer:users!buyer_id(name, handle, avatar_url)")
          .eq("seller_id", sellerRow.id)
          .order("created_at", { ascending: false })
          .limit(30);
        (bks || []).forEach(b => items.push({
          id: `bk-${b.id}`, type: "booking",
          text: `${b.buyer?.handle || "Someone"} booked ${b.listings?.name || "your listing"}`,
          sub: b.status,
          at: b.created_at,
          avatarUrl: b.buyer?.avatar_url || null,
          initials: b.buyer?.name ? b.buyer.name[0].toUpperCase() : "?",
        }));

        const { data: fols } = await supabase
          .from("follows")
          .select("created_at, follower_id")
          .eq("seller_id", sellerRow.id)
          .order("created_at", { ascending: false })
          .limit(30);
        if (fols?.length) {
          const folIds = fols.map(f => f.follower_id);
          const { data: folUsers } = await supabase
            .from("users").select("id, name, handle, avatar_url").in("id", folIds);
          const byId = Object.fromEntries((folUsers || []).map(u => [u.id, u]));
          fols.forEach(f => {
            const u = byId[f.follower_id];
            items.push({
              id: `fol-${f.follower_id}-${f.created_at}`, type: "follow",
              text: `${u?.handle || "Someone"} followed you`,
              sub: null,
              at: f.created_at,
              avatarUrl: u?.avatar_url || null,
              initials: u?.name ? u.name[0].toUpperCase() : "?",
            });
          });
        }

        const { data: revs } = await supabase
          .from("reviews")
          .select("id, rating, body, created_at, reviewer:users!buyer_id(name, handle, avatar_url)")
          .eq("seller_id", sellerRow.id)
          .order("created_at", { ascending: false })
          .limit(30);
        (revs || []).forEach(r => items.push({
          id: `rev-${r.id}`, type: "review",
          text: `${r.reviewer?.handle || "Someone"} left you a ${r.rating}★ review`,
          sub: r.body,
          at: r.created_at,
          avatarUrl: r.reviewer?.avatar_url || null,
          initials: r.reviewer?.name ? r.reviewer.name[0].toUpperCase() : "?",
        }));
      }

      // My bookings that got confirmed
      const { data: myBks } = await supabase
        .from("bookings")
        .select("id, created_at, listings(name), seller:sellers(users(name, handle, avatar_url))")
        .eq("buyer_id", uid)
        .eq("status", "confirmed")
        .order("created_at", { ascending: false })
        .limit(20);
      (myBks || []).forEach(b => {
        const su = b.seller?.users;
        items.push({
          id: `mybk-${b.id}`, type: "confirmed",
          text: `${su?.handle || "Your seller"} confirmed your booking for ${b.listings?.name || "a listing"}`,
          sub: null,
          at: b.created_at,
          avatarUrl: su?.avatar_url || null,
          initials: su?.name ? su.name[0].toUpperCase() : "?",
        });
      });

      items.sort((a, b) => new Date(b.at) - new Date(a.at));
      setNotifs(items);
      if (items.length) {
        const lastSeen = localStorage.getItem('nearme_notifs_seen');
        const newest = items[0].at;
        if (!lastSeen || new Date(newest) > new Date(lastSeen)) setHasUnread(true);
      }
    } catch (err) {
      console.error("notifs error", err);
    }
    setNotifsLoading(false);
  };


  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        const { latitude, longitude } = pos.coords;
        setUserCoords({ lat: latitude, lng: longitude });
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
        const data = await res.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || "Nearby";
        const state = data.address?.state_code || data.address?.state || "";
        setCityLabel(state ? `${city}, ${state}` : city);
      } catch { /* silently fail */ }
    }, () => { /* permission denied — keep "Nearby" */ });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Try nested join first (requires FK constraints in PostgREST schema)
        const { data, error } = await supabase
          .from("listings")
          .select("*, sellers!seller_id(id, rating, review_count, retention_pct, location, users!user_id(id, handle, name, avatar_url))")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(100);

        if (error) {
          console.error("listings join error:", error);
          // Fallback: fetch listings and sellers separately, join in JS
          const { data: rawListings } = await supabase
            .from("listings").select("*").eq("is_active", true)
            .order("created_at", { ascending: false }).limit(100);
          if (!rawListings?.length) { setDbLoading(false); return; }
          const sellerIds = [...new Set(rawListings.map(l => l.seller_id))];
          const { data: rawSellers } = await supabase
            .from("sellers").select("id, rating, review_count, retention_pct, location, user_id")
            .in("id", sellerIds);
          const userIds = [...new Set((rawSellers||[]).map(s => s.user_id))];
          const { data: rawUsers } = await supabase
            .from("users").select("id, handle, name, avatar_url").in("id", userIds);
          const usersById = Object.fromEntries((rawUsers||[]).map(u => [u.id, u]));
          const sellersById = Object.fromEntries((rawSellers||[]).map(s => [s.id, { ...s, users: usersById[s.user_id] || {} }]));
          const joined = rawListings.map(l => ({ ...l, sellers: sellersById[l.seller_id] || {} }));
          setDbListings(joined);
        } else {
          setDbListings(data || []);
        }
      } catch (e) {
        console.error("listings fetch error:", e);
      }
      setDbLoading(false);
    })();
  }, [dbVersion]);

  useEffect(() => { if (!guest) loadNotifs(); }, [guest]);

  useEffect(() => {
    if (!initialListingId) return;
    (async () => {
      try {
        const { data: l } = await supabase.from("listings").select("*, sellers!seller_id(id, rating, review_count, retention_pct, location, users!user_id(id, handle, name, avatar_url))").eq("id", initialListingId).maybeSingle();
        if (!l) return;
        const s = l.sellers; const u = s?.users;
        const nm = u?.name || "Seller";
        const ini = nm.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
        const seller = { id: s?.id, userId: u?.id, isReal: true, isSeller: true, name: nm, handle: u?.handle||"", avatar: ini, avatarUrl: u?.avatar_url||null, bio: "", trade: l.category||"", distance: null, rating: s?.rating||0, reviews: s?.review_count||0, retention: s?.retention_pct||0, followers: 0, promoted: false, verified: false, publicClients: [], posts: [], services: [] };
        setSelected({ name: l.name, price: l.price, slot: l.slot_minutes??0, flash: l.is_flash, kind: l.kind, seller, lid: l.id, image_urls: l.image_urls||[], description: l.description||"" });
      } catch {}
    })();
  }, [initialListingId]);

  const initialHandleOpened = useRef(false);
  useEffect(() => {
    if (!initialHandle || initialHandleOpened.current || !dbListings.length) return;
    const match = dbListings.map(_norm).find(l =>
      (l.seller.handle||'').replace(/^@/,'').toLowerCase() === initialHandle.toLowerCase()
    );
    if (match) { initialHandleOpened.current = true; openSeller(match.seller); }
  }, [initialHandle, dbListings]);

  useEffect(() => {
    if (tab === "seller" && mySellerProfile === null) {
      (async () => {
        try {
          const user = await getCurrentUser();
          if (!user) { setMySellerProfile(false); return; }
          setSellUserId(user.id);
          const { data } = await supabase
            .from("sellers")
            .select("id, trade, seller_tag, parent_cat")
            .eq("user_id", user.id)
            .maybeSingle();
          setMySellerProfile(data || false);
          // Sync agreement state from existing profile
          if (data) {
            const pc = data.parent_cat;
            if (pc === "Goods" || pc === "Both") { localStorage.setItem(`nm_agreed_goods_${user.id}`, "1"); setGoodsAgreed(true); }
            if (pc === "Services" || pc === "Both") { localStorage.setItem(`nm_agreed_services_${user.id}`, "1"); setServicesAgreed(true); }
          } else {
            setGoodsAgreed(!!localStorage.getItem(`nm_agreed_goods_${user.id}`));
            setServicesAgreed(!!localStorage.getItem(`nm_agreed_services_${user.id}`));
          }
        } catch { setMySellerProfile(false); }
      })();
    }
  }, [tab, mySellerProfile]);

  useEffect(() => {
    if (userProfile && myListings === null) {
      (async () => {
        try {
          const user = await getCurrentUser();
          if (!user) { setMyListings([]); return; }
          const { data: seller } = await supabase
            .from("sellers").select("id").eq("user_id", user.id).maybeSingle();
          if (!seller) { setMyListings([]); return; }
          const { data: listings } = await supabase
            .from("listings").select("*").eq("seller_id", seller.id).order("created_at", { ascending: false });
          setMyListings(listings || []);
        } catch { setMyListings([]); }
      })();
    }
  }, [userProfile, myListings]);

  useEffect(() => {
    if (!sellerView?.isReal) { setSellerListings([]); return; }
    setSellerListings([]);
    setSellerListingsLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from("listings")
          .select("*")
          .eq("seller_id", sellerView.id)
          .eq("is_active", true)
          .order("created_at", { ascending: false });
        setSellerListings(data || []);
      } catch { setSellerListings([]); }
      setSellerListingsLoading(false);
    })();
  }, [sellerView?.id]);

  const searchDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(searchDebounce.current);
    const sq = searchQuery.trim().toLowerCase();
    if (sq.length < 2) { setDbUserResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      try {
        // Query sellers joined with users — sellers table is public-readable
        // (same join that the listings query already uses successfully)
        // Fetch sellers + users separately to avoid FK join issues
        const { data: sellerRows } = await supabase
          .from("sellers")
          .select("id, trade, seller_tag, rating, review_count, retention_pct, user_id")
          .limit(100);

        const userIds = [...new Set((sellerRows||[]).map(s => s.user_id).filter(Boolean))];
        const { data: userRows } = await supabase
          .from("users").select("id, handle, name, bio, avatar_url").in("id", userIds);
        const usersById = Object.fromEntries((userRows||[]).map(u => [u.id, u]));

        const sellerMatches = (sellerRows || [])
          .map(row => ({ ...row, u: usersById[row.user_id] }))
          .filter(row => {
            if (!row.u) return false;
            const hay = `${row.u.name||""} ${row.u.handle||""} ${row.u.bio||""} ${row.trade||""}`.toLowerCase();
            return hay.includes(sq);
          })
          .map(row => {
            const nm = row.u?.name || "Seller";
            const ini = nm.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
            return {
              id: row.id, userId: row.u?.id,
              isReal: true, isSeller: true,
              name: nm, handle: row.u?.handle || "@user", avatar: ini, avatarUrl: row.u?.avatar_url || null,
              bio: row.u?.bio || "", trade: row.trade || "", tag: row.seller_tag || "",
              campus: null, distance: null, rating: row.rating || 0,
              reviews: row.review_count || 0, retention: row.retention_pct || 0,
              followers: 0, promoted: false, verified: false, publicClients: [],
              posts: [], services: [],
            };
          });

        setDbUserResults(sellerMatches);
      } catch (e) { console.error("user search error", e); setDbUserResults([]); }
    }, 300);
    return () => clearTimeout(searchDebounce.current);
  }, [searchQuery]);

  const handleDeleteListing = async (id) => {
    try {
      await supabase.from("listings").delete().eq("id", id);
      setMyListings(prev => prev ? prev.filter(l => l.id !== id) : prev);
      setDbVersion(v => v + 1);
    } catch { /* silently ignore */ }
    setDeletingId(null);
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    try {
      await supabase.from("listings").delete().in("id", ids);
      setMyListings(prev => prev ? prev.filter(l => !ids.includes(l.id)) : prev);
      setDbVersion(v => v + 1);
    } catch { /* silently ignore */ }
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  const toggleSelectId = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const myListingToSelected = (l) => ({
    name: l.name, price: l.price, slot: l.slot_minutes ?? 0,
    flash: l.is_flash || false, kind: l.kind, lid: l.id,
    image_urls: l.image_urls || [], description: l.description || "",
    seller: {
      id: mySellerProfile?.id, isReal: true,
      name: me.name, handle: me.handle, avatar: me.avatar, bio: me.bio,
      trade: l.category || "", campus: null, distance: null,
      rating: 0, reviews: 0, retention: 0, followers: 0,
      promoted: false, verified: false, publicClients: [],
      posts: [{ id: 1, type: l.kind === "product" ? "product" : "listing", img: l.image_urls?.[0] || null }],
      services: [],
    }
  });

  const resetSellFlow = () => {
    setSellStep("type"); setSellKind(null);
    setPendingDisclosure(null); setDisclosureChecked(new Set());
    setListingName(""); setListingDesc(""); setListingPrice("");
    setListingCat(""); setListingSlot(0); setListingFlash(false);
    listingPhotoURLs.forEach(url => URL.revokeObjectURL(url));
    setListingPhotoFiles([]); setListingPhotoURLs([]);
    setSellError("");
  };

  const handleTypePick = (kind) => { setSellKind(kind); setSellStep("form"); setListingCat(""); };

  const handleAgree = async (side) => {
    setAgreeLoading(side);
    // Save agreement immediately — don't let DB failures block it
    const user = await getCurrentUser();
    const uid = user?.id || sellUserId;
    if (uid) localStorage.setItem(`nm_agreed_${side}_${uid}`, "1");
    if (side === "goods") setGoodsAgreed(true); else setServicesAgreed(true);

    // Create/update seller profile in background
    if (uid) {
      const pc = side === "goods" ? "Goods" : "Services";
      try {
        if (!mySellerProfile) {
          try {
            const profile = await createSellerProfile({ trade: pc, parent_cat: pc, seller_tag: "Side Hustler", lat: userCoords?.lat, lng: userCoords?.lng });
            setMySellerProfile(profile);
          } catch {
            // Profile may already exist — fetch it
            const { data } = await supabase.from("sellers").select("id, trade, seller_tag, parent_cat").eq("user_id", uid).maybeSingle();
            if (data) setMySellerProfile(data);
          }
        } else if (mySellerProfile.parent_cat !== "Both" && mySellerProfile.parent_cat !== pc) {
          const { data } = await supabase.from("sellers").update({ parent_cat: "Both" }).eq("id", mySellerProfile.id).select().single();
          if (data) setMySellerProfile(data);
        }
      } catch (err) {
        console.error("seller profile update error", err);
      }
    }
    setAgreeLoading(null);
  };

  const handleRevoke = (side) => {
    if (sellUserId) localStorage.removeItem(`nm_agreed_${side}_${sellUserId}`);
    if (side === "goods") setGoodsAgreed(false); else setServicesAgreed(false);
  };

  const handleCatSelect = (cat) => {
    setListingCat(cat);
    const disc = CATEGORY_DISCLOSURES[cat];
    if (disc && !hasDisc(sellUserId, cat)) {
      setPendingDisclosure({ cat, ...disc });
      setDisclosureChecked(new Set());
      setSellStep("disclosure");
    }
  };

  const handlePhotoAdd = e => {
    const toAdd = Array.from(e.target.files).slice(0, 5 - listingPhotoFiles.length);
    if (!toAdd.length) return;
    setListingPhotoURLs(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))]);
    setListingPhotoFiles(prev => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const removePhoto = idx => {
    URL.revokeObjectURL(listingPhotoURLs[idx]);
    setListingPhotoFiles(prev => prev.filter((_,i) => i !== idx));
    setListingPhotoURLs(prev => prev.filter((_,i) => i !== idx));
  };

  const movePhoto = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= listingPhotoURLs.length) return;
    setListingPhotoFiles(prev => { const a=[...prev]; [a[idx],a[newIdx]]=[a[newIdx],a[idx]]; return a; });
    setListingPhotoURLs(prev => { const a=[...prev]; [a[idx],a[newIdx]]=[a[newIdx],a[idx]]; return a; });
  };

  const handleSellSubmit = async () => {
    if (!listingName.trim() || !listingPrice || !listingCat) {
      setSellError("Please fill in the name, category, and price."); return;
    }
    setSellLoading(true); setSellError("");
    try {
      // Ensure we have a seller profile — create one if missing
      let sellerId = mySellerProfile?.id;
      if (!sellerId) {
        const user = await getCurrentUser();
        if (!user) throw new Error("Not signed in.");
        const { data: existing } = await supabase.from("sellers").select("id").eq("user_id", user.id).maybeSingle();
        if (existing) {
          sellerId = existing.id;
          setMySellerProfile(existing);
        } else {
          const pc = sellKind === "goods" ? "Goods" : "Services";
          const profile = await createSellerProfile({ trade: listingCat || pc, parent_cat: pc, seller_tag: "Side Hustler", lat: userCoords?.lat, lng: userCoords?.lng });
          sellerId = profile.id;
          setMySellerProfile(profile);
        }
      }

      if (!sellerId) throw new Error("Seller profile not found. Go back, agree to seller terms, and try again.");

      const imageUrls = [];
      for (const file of listingPhotoFiles) {
        const url = await uploadPhoto(file, "listings");
        imageUrls.push(url);
      }
      await createListing({
        seller_id: sellerId,
        name: listingName.trim(),
        description: listingDesc.trim() || null,
        price: parseFloat(listingPrice),
        kind: sellKind === "goods" ? "product" : "service",
        category: listingCat,
        image_urls: imageUrls,
        slot_minutes: sellKind === "service" ? listingSlot : null,
        is_active: true,
        is_flash: listingFlash,
        message_to_book: sellKind === "service" ? listingMsgToBook : false,
      });
      setSellStep("done");
      setDbVersion(v => v + 1); // refresh explore listings
      setMyListings(null);       // refresh profile listings tab
    } catch (err) {
      setSellError(err.message || err.error_description || "Something went wrong. Try again.");
    }
    setSellLoading(false);
  };

  const toggleFollow = id => setFollowing(f => { const s=new Set(f); s.has(id)?s.delete(id):s.add(id); return s; });
  const toggleSave   = (e,id) => { e.stopPropagation(); setSaved(s => { const n=new Set(s); n.has(id)?n.delete(id):n.add(id); return n; }); };

  const _hav = (la1,lo1,la2,lo2) => { const R=3959,dL=(la2-la1)*Math.PI/180,dO=(lo2-lo1)*Math.PI/180,a=Math.sin(dL/2)**2+Math.cos(la1*Math.PI/180)*Math.cos(la2*Math.PI/180)*Math.sin(dO/2)**2; return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a)); };
  const _pt  = pt => { const m=pt?.match(/POINT\(([^ ]+) ([^ )]+)\)/); return m?{lng:+m[1],lat:+m[2]}:null; };
  const _norm = l => {
    const s=l.sellers||{}, u=s.users||{};
    const nm=u.name||"Seller", ini=nm.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
    const loc=_pt(s.location), dist=loc&&userCoords?Math.round(_hav(userCoords.lat,userCoords.lng,loc.lat,loc.lng)*10)/10:null;
    const img=l.image_urls?.[0]||null;
    return { name:l.name, price:l.price, slot:l.slot_minutes??0, flash:l.is_flash||false, kind:l.kind,
      seller:{ id:s.id||l.seller_id, userId:u.id||null, isReal:true, name:nm, handle:u.handle||"@seller", avatar:ini, avatarUrl:u.avatar_url||null, bio:"", trade:l.category||"",
        campus:null, distance:dist, rating:s.rating||0, reviews:s.review_count||0, retention:s.retention_pct||0,
        followers:0, promoted:false, verified:false, publicClients:[],
        posts:[{id:1,type:l.kind==="product"?"product":"listing",img}], services:[] },
      lid:l.id, image_urls:l.image_urls||[], description:l.description||"", messageToBook:l.message_to_book||false };
  };
  const allListings = dbListings.map(_norm);

  // first image per category for the desktop landing cards
  const catCoverImg = allListings.reduce((acc, l) => {
    const cat = l.seller.trade;
    if (cat && !acc[cat] && l.image_urls?.[0]) acc[cat] = l.image_urls[0];
    return acc;
  }, {});

  // filter by kind tab
  const kindFiltered = exploreKind==="both" ? allListings : allListings.filter(l => exploreKind==="services" ? l.kind==="service" : l.kind==="product");
  const catOptions   = CORE_CATS;

  const filtered = kindFiltered
    .filter(l => subCat.size===0 || subCat.has(l.seller.trade) || subCat.has(l.seller.parentCat))
    .filter(l => !flashOnly   || l.flash)
    .filter(l => campusMode   ? !!l.seller.campus : (radiusFilter === null || l.seller.distance === null || l.seller.distance <= radiusFilter))
    .sort((a,b) => sort==="Quickest"?a.slot-b.slot:sort==="Closest"?(a.seller.distance??99)-(b.seller.distance??99):sort==="Price ↑"?a.price-b.price:sort==="Top Rated"?b.seller.rating-a.seller.rating:b.seller.retention-a.seller.retention);

  const forYou = allListings.filter(l => me.interests.includes(l.seller.trade)).sort((a,b) => b.seller.rating-a.seller.rating);

  // ── search ──
  const sq = searchQuery.trim().toLowerCase();
  const mq = s => !!(s && s.toLowerCase().includes(sq));
  const searchSvcResults   = sq.length < 2 ? [] : allListings.filter(l => l.kind==="service" && (mq(l.name)||mq(l.seller.name)||mq(l.seller.handle)||mq(l.seller.bio)||mq(l.seller.trade)));
  const searchGoodsResults = sq.length < 2 ? [] : allListings.filter(l => l.kind==="product"  && (mq(l.name)||mq(l.seller.name)||mq(l.seller.handle)||mq(l.seller.bio)||mq(l.seller.trade)));
  // People: DB user search merged with listing-derived sellers (DB results take priority, dedup by id)
  const searchUserResults = sq.length < 2 ? [] : (() => {
    const seen = new Set(dbUserResults.map(u => u.id));
    const listingSellers = Object.values(allListings.reduce((acc,l) => { if(!acc[l.seller.id]) acc[l.seller.id]=l.seller; return acc; }, {}))
      .filter(s => (mq(s.name)||mq(s.handle)||mq(s.bio)||mq(s.trade)) && !seen.has(s.id));
    return [...dbUserResults, ...listingSellers];
  })();
  const applySearchSort = (arr, isUser=false) => {
    if (searchSort==="distance") return [...arr].sort((a,b)=>(isUser?a:a.seller).distance-(isUser?b:b.seller).distance);
    if (searchSort==="rating")   return [...arr].sort((a,b)=>(isUser?b:b.seller).rating-(isUser?a:a.seller).rating);
    if (searchSort==="price_asc"  && !isUser) return [...arr].sort((a,b)=>a.price-b.price);
    if (searchSort==="price_desc" && !isUser) return [...arr].sort((a,b)=>b.price-a.price);
    return arr;
  };
  const sortedSvcResults   = applySearchSort(searchSvcResults);
  const sortedGoodsResults = applySearchSort(searchGoodsResults);
  const sortedUserResults  = applySearchSort(searchUserResults, true);

  /* ── GRID CARD ── */
  const Card = ({l}) => (
    <div onClick={()=>openListing(l)} style={{background:D.white,border:`1px solid ${D.gray200}`,borderRadius:D.radius,overflow:"hidden",cursor:"pointer"}}>
      {/* image */}
      <div style={{position:"relative",aspectRatio:"1/1",background:D.gray100,overflow:"hidden"}}>
        {l.seller.posts[0]?.img
          ? <img src={l.seller.posts[0].img} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
          : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,color:D.gray400}}>{l.kind==="product"?"📦":"🛠️"}</div>
        }
        {/* badges */}
        {l.flash && !isProd(l) && (
          <div style={{position:"absolute",top:7,left:7,background:D.red,borderRadius:D.radiusSm,padding:"3px 7px",display:"flex",alignItems:"center",gap:4}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:D.white,animation:"fp 1.4s ease-in-out infinite"}}/>
            <span style={{fontSize:9,fontWeight:800,color:D.white,letterSpacing:"0.06em"}}>FLASH</span>
          </div>
        )}
        {isProd(l) && (
          <div style={{position:"absolute",top:7,left:7,background:D.black,borderRadius:D.radiusSm,padding:"3px 7px"}}>
            <span style={{fontSize:9,fontWeight:800,color:D.white,letterSpacing:"0.06em"}}>GOODS</span>
          </div>
        )}
        {l.seller.promoted && (
          <div style={{position:"absolute",top:7,right:36,background:"rgba(255,255,255,0.92)",borderRadius:D.radiusSm,padding:"3px 7px",border:`1px solid ${D.gray200}`}}>
            <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.06em"}}>PRO</span>
          </div>
        )}
        {/* save */}
        <button onClick={e=>{e.stopPropagation();requireAuth(()=>toggleSave(e,l.lid));}} style={{position:"absolute",top:6,right:6,width:28,height:28,background:"rgba(255,255,255,0.92)",border:`1px solid ${D.gray200}`,borderRadius:D.radiusSm,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,cursor:"pointer"}}>
          {saved.has(l.lid)?"♥":"♡"}
        </button>
      </div>

      {/* info — separated from image with border */}
      <div style={{padding:"9px 10px 11px",borderTop:`1px solid ${D.gray200}`}}>
        {/* listing name */}
        <div style={{fontSize:12,fontWeight:700,letterSpacing:"-0.01em",marginBottom:5,lineHeight:1.3,color:D.black}}>{l.name}</div>

        {/* seller handle row */}
        <div onClick={e=>{e.stopPropagation();openSeller(l.seller);}} style={{display:"flex",alignItems:"center",gap:5,marginBottom:6,cursor:"pointer"}}>
          <Avatar url={l.seller.avatarUrl} initials={l.seller.avatar} size={18} radius={D.radiusSm}/>
          <span style={{fontSize:11,fontWeight:600,color:D.gray600}}>{l.seller.handle}</span>
          {l.seller.verified && <span style={{fontSize:9,fontWeight:800,background:D.black,color:D.white,padding:"1px 4px",borderRadius:2}}>✓</span>}
        </div>

        {/* price + rating */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: isProd(l)?0:7}}>
          <span style={{fontSize:15,fontWeight:900,letterSpacing:"-0.02em"}}>${l.price}</span>
          <span style={{fontSize:10,fontWeight:600,color:D.gray600}}>★ {l.seller.rating}</span>
        </div>

        {/* availability + distance — under the listing info, bordered */}
        {!isProd(l) && (
          <div style={{display:"flex",gap:6,paddingTop:7,borderTop:`1px solid ${D.gray100}`}}>
            <div style={{display:"flex",alignItems:"center",gap:4,flex:1}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:slotColor(l.slot),flexShrink:0}}/>
              <span style={{fontSize:10,fontWeight:700,color:slotColor(l.slot)}}>{slotLabel(l.slot)}</span>
            </div>
            <span style={{fontSize:10,color:D.gray400,fontWeight:600}}>{l.seller.distance!==null?`📍 ${l.seller.distance}mi`:"📍 Local"}</span>
          </div>
        )}

        {/* goods: fulfillment info */}
        {isProd(l) && (
          <div style={{paddingTop:7,borderTop:`1px solid ${D.gray100}`}}>
            <span style={{fontSize:10,color:D.gray600,fontWeight:600}}>📍 {l.seller.distance}mi · {l.seller.location}</span>
          </div>
        )}
      </div>
    </div>
  );

  /* ── FEED POST ── */
  const Post = ({post}) => (
    <div style={{borderBottom:`1px solid ${D.gray100}`}}>
      <div onClick={()=>openSeller(post.seller)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",cursor:"pointer"}}>
        <Avatar url={post.seller.avatarUrl} initials={post.seller.avatar} size={32}/>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:800,letterSpacing:"-0.01em"}}>{post.seller.name}</div>
          <div style={{fontSize:11,color:D.gray600,fontWeight:500}}>{post.seller.handle} · {post.seller.trade}</div>
        </div>
        {post.flash && (
          <div style={{background:D.red,borderRadius:D.radiusSm,padding:"3px 8px",display:"flex",alignItems:"center",gap:4}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:D.white,animation:"fp 1.4s ease-in-out infinite"}}/>
            <span style={{fontSize:9,fontWeight:800,color:D.white,letterSpacing:"0.06em"}}>FLASH</span>
          </div>
        )}
        {post.type==="product" && !post.flash && (
          <div style={{background:D.black,borderRadius:D.radiusSm,padding:"3px 8px"}}>
            <span style={{fontSize:9,fontWeight:800,color:D.white,letterSpacing:"0.06em"}}>GOODS</span>
          </div>
        )}
      </div>
      <div style={{aspectRatio:"1/1",background:D.gray100,overflow:"hidden"}}>
        <img src={post.img} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
      </div>
      <div style={{padding:"10px 16px 12px"}}>
        <div style={{fontSize:13,marginBottom:8,fontWeight:500,lineHeight:1.4,color:D.gray800}}>{post.caption}</div>
        {(post.type==="listing"||post.type==="product") && (
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {post.type!=="product" && (
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:slotColor(post.slot||0)}}/>
                <span style={{fontSize:11,fontWeight:700,color:slotColor(post.slot||0)}}>{slotLabel(post.slot||0)}</span>
              </div>
            )}
            <button disabled style={{background:D.gray200,color:D.gray400,border:"none",borderRadius:D.radius,padding:"8px 18px",fontSize:12,fontWeight:800,cursor:"not-allowed",fontFamily:"Noto Sans,sans-serif",marginLeft:"auto"}}>
              {post.type==="product"?"Buy":"Book"} · ${post.price}
            </button>
          </div>
        )}
        {post.type==="photo" && (
          <div style={{display:"flex",gap:16}}>
            <button style={{fontSize:16,background:"none",border:"none",cursor:"pointer"}}>♡</button>
            <button style={{fontSize:16,background:"none",border:"none",cursor:"pointer"}}>💬</button>
          </div>
        )}
      </div>
    </div>
  );

  /* ── SHARED HEADER ── */
  const AppHeader = ({title, right}) => (
    <div style={{padding:"12px 16px 10px",borderBottom:`1px solid ${D.gray200}`,background:D.white,position:"sticky",top: isDesktop ? 56 : 0,zIndex:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.05em"}}>{title}<span style={{color:D.red}}>.</span></div>
        {right}
      </div>
    </div>
  );

  /* ── KIND TAB BAR ── (Services / Goods) */
  const KindTabs = ({value, onChange}) => (
    <div style={{display:"flex",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
      {[["services","🛠 Services","Skills & appointments"],["both","Both",""],["goods","📦 Goods","Products & items"]].map(([key,label,sub]) => {
        const isBoth = key==="both";
        return (
          <div key={key} onClick={()=>{onChange(key);setSubCat(new Set());}}
            style={{flex:isBoth?0:1,flexShrink:0,padding:isBoth?"11px 10px":"11px 14px",cursor:"pointer",
              borderBottom:value===key?`2.5px solid ${D.black}`:"2.5px solid transparent",
              borderLeft:key==="goods"?`1px solid ${D.gray200}`:"none",
              borderRight:key==="services"?`1px solid ${D.gray200}`:"none",
              background:D.white,textAlign:"center"}}>
            <div style={{fontSize:isBoth?10:13,fontWeight:800,letterSpacing:"-0.01em",color:value===key?D.black:D.gray400,marginBottom:isBoth?0:2,whiteSpace:"nowrap"}}>{label}</div>
            {!isBoth && <div style={{fontSize:10,color:value===key?D.gray600:D.gray400,fontWeight:500}}>{sub}</div>}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{maxWidth: isDesktop ? "100%" : 430,margin:"0 auto",minHeight:"100vh",background:D.gray50,fontFamily:"'Noto Sans',sans-serif",color:D.black,position:"relative"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700;800;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        input,select,textarea{font-family:'Noto Sans',sans-serif}
        input:focus,select:focus,textarea:focus{outline:none}
        @keyframes fp{0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes su{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes sh{from{transform:translateY(100%)}to{transform:translateY(0)}}
        .fade{animation:su .2s ease both}
        .g2>*{animation:su .22s ease both}
        .g2>*:nth-child(1){animation-delay:.03s}.g2>*:nth-child(2){animation-delay:.06s}
        .g2>*:nth-child(3){animation-delay:.09s}.g2>*:nth-child(4){animation-delay:.12s}
        .g2>*:nth-child(5){animation-delay:.15s}.g2>*:nth-child(6){animation-delay:.18s}
        .g2>*:nth-child(7){animation-delay:.21s}.g2>*:nth-child(8){animation-delay:.24s}
        .sheet{animation:sh .3s cubic-bezier(.16,1,.3,1) both}
        .tap{cursor:pointer;-webkit-tap-highlight-color:transparent}
        .tap:active{opacity:.7}
        .hvr:hover{background:#f5f5f5}
      `}</style>

      {/* ══ DESKTOP TOP NAV ════════════════ */}
      {isDesktop && (
        <div style={{position:"fixed",top:0,left:0,right:0,height:60,background:D.white,borderBottom:`1px solid ${D.gray200}`,zIndex:200}}>
          <div style={{height:"100%",display:"flex",alignItems:"center",padding:"0 32px"}}>
            {/* Left: Logo + Nav tabs */}
            <div style={{display:"flex",alignItems:"center",gap:0,flexShrink:0,marginRight:16}}>
              <div className="tap" onClick={()=>{setTab("explore");setSearchActive(false);setSearchQuery("");setDesktopLandingTab("all");}} style={{fontSize:20,fontWeight:900,letterSpacing:"-0.05em",marginRight:8,flexShrink:0}}>nearme<span style={{color:D.red}}>.</span></div>
              {[{key:"feed",label:"Feed"},{key:"explore",label:"Explore"}].map(n=>(
                <button key={n.key} onClick={()=>{
                  if(n.key==="feed"){requireAuth(()=>{setTab(n.key);setSearchActive(false);setSearchQuery("");});return;}
                  setTab(n.key);setSearchActive(false);setSearchQuery("");if(n.key==="explore")setDesktopLandingTab("all");
                }}
                  style={{padding:"0 12px",height:60,border:"none",background:"none",fontSize:13,fontWeight:tab===n.key?800:500,color:tab===n.key?D.black:D.gray400,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",borderBottom:tab===n.key?`2.5px solid ${D.red}`:"2.5px solid transparent",flexShrink:0,letterSpacing:"-0.01em"}}>
                  {n.label}
                </button>
              ))}
            </div>
            {/* Center: Search + Filters */}
            <div style={{flex:1,display:"flex",alignItems:"center",gap:8}}>
              <div style={{flex:1,position:"relative"}}>
                <svg style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={D.gray400} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input value={searchQuery} onChange={e=>{setSearchQuery(e.target.value);setSearchActive(e.target.value.length>0);if(e.target.value.length>0)setTab("explore");}} onFocus={()=>{if(searchQuery.length>0)setSearchActive(true);}} placeholder="Search listings, people…" style={{width:"100%",padding:"9px 32px 9px 34px",border:`1.5px solid ${D.gray200}`,borderRadius:24,fontSize:13,fontFamily:"Noto Sans,sans-serif",outline:"none",color:D.black,background:D.gray50}}/>
                {searchQuery.length>0&&<div className="tap" onClick={()=>{setSearchQuery("");setSearchActive(false);}} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:12,color:D.gray400,cursor:"pointer"}}>✕</div>}
              </div>
              <button onClick={()=>{setSliderRadius(radiusFilter||1);setShowRadiusModal(true);}} style={{padding:"8px 12px",border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,background:D.white,color:D.black,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",display:"flex",alignItems:"center",gap:5,flexShrink:0,whiteSpace:"nowrap"}}>
                <span>📍</span>
                <span>{cityLabel}</span>
              </button>
              <div style={{position:"relative",flexShrink:0}}>
                <button onClick={()=>setShowFilterDropdown(d=>!d)} style={{padding:"8px 14px",border:`1.5px solid ${showFilterDropdown||subCat.size>0||exploreKind!=="both"?D.black:D.gray200}`,borderRadius:D.radius,background:showFilterDropdown||subCat.size>0||exploreKind!=="both"?D.black:D.white,color:showFilterDropdown||subCat.size>0||exploreKind!=="both"?D.white:D.black,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",display:"flex",alignItems:"center",gap:6}}>
                  Filters{subCat.size>0||exploreKind!=="both"?" ·":""} <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor"><path d="M5 6L0 0h10z"/></svg>
                </button>
                {showFilterDropdown&&(
                  <>
                    <div onClick={()=>setShowFilterDropdown(false)} style={{position:"fixed",inset:0,zIndex:1}}/>
                    <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:D.white,border:`1px solid ${D.gray200}`,borderRadius:D.radius,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",padding:"16px 16px 12px",width:300,zIndex:2}}>
                      <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,marginBottom:8}}>TYPE</div>
                      <div style={{display:"flex",gap:6,marginBottom:14}}>
                        {[["both","All"],["services","Services"],["goods","Goods"]].map(([k,label])=>(
                          <button key={k} onClick={()=>{setExploreKind(k);setSubCat(new Set());}} style={{flex:1,padding:"7px 0",border:`1.5px solid ${exploreKind===k?D.black:D.gray200}`,borderRadius:D.radiusSm,background:exploreKind===k?D.black:D.white,color:exploreKind===k?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>{label}</button>
                        ))}
                      </div>
                      {(exploreKind==="goods"||exploreKind==="both")&&(<>
                        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,marginBottom:8}}>FULFILLMENT</div>
                        <div style={{display:"flex",gap:6,marginBottom:14}}>
                          {[["pickup","📍 Pickup"],["delivery","🛵 Delivery"],["mail","📬 Mail"]].map(([k,label])=>(
                            <button key={k} onClick={()=>setGoodsFulfill(k)} style={{flex:1,padding:"7px 0",border:`1.5px solid ${goodsFulfill===k?D.black:D.gray200}`,borderRadius:D.radiusSm,background:goodsFulfill===k?D.black:D.white,color:goodsFulfill===k?D.white:D.gray800,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>{label}</button>
                          ))}
                        </div>
                      </>)}
                      <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,marginBottom:8}}>SORT</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
                        {SORT_OPTS.map(o=>(
                          <button key={o} onClick={()=>setSort(o)} style={{padding:"6px 10px",border:`1.5px solid ${sort===o?D.black:D.gray200}`,borderRadius:D.radiusSm,background:sort===o?D.black:D.white,color:sort===o?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",whiteSpace:"nowrap"}}>{o}</button>
                        ))}
                      </div>
                      {(subCat.size>0||exploreKind!=="both"||sort!=="Quickest")&&(
                        <button onClick={()=>{setSubCat(new Set());setExploreKind("both");setSort("Quickest");setGoodsFulfill("pickup");setShowFilterDropdown(false);}} style={{width:"100%",padding:"8px",border:`1.5px solid ${D.gray200}`,borderRadius:D.radiusSm,background:D.white,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",color:D.gray600}}>Reset all filters</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Right: + List, Bell, Avatar (or Log in / Sign up for guests) */}
            {guest ? (
              <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:16,flexShrink:0}}>
                <button onClick={()=>onSignIn?.()} style={{padding:"8px 18px",border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,background:D.white,color:D.black,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",letterSpacing:"0.02em"}}>Log in</button>
                <button onClick={()=>onSignIn?.()} style={{padding:"8px 18px",border:"none",borderRadius:D.radius,background:D.black,color:D.white,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",letterSpacing:"0.02em"}}>Sign up</button>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:16,flexShrink:0}}>
                <button onClick={()=>setTab("seller")} style={{padding:"8px 18px",border:"none",borderRadius:D.radius,background:D.black,color:D.white,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",letterSpacing:"0.02em"}}>+ List</button>
                <div className="tap" onClick={()=>{setShowNotifs(true);loadNotifs();}} style={{width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <div className="tap" onClick={()=>setUserProfile(true)}><Avatar url={me.avatarUrl} initials={me.avatar} size={30}/></div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{height: isDesktop ? 60 : 44}}/>

      {/* ══ EXPLORE ══════════════════════════ */}
      {tab==="explore" && !sellerView && !userProfile && (
        <div className="fade">
          {/* Header - mobile only */}
          {!isDesktop && <div style={{padding:"12px 16px 10px",borderBottom:`1px solid ${D.gray200}`,background:D.white,position:"sticky",top:0,zIndex:10}}>
            {!searchActive && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.05em"}}>nearme<span style={{color:D.red}}>.</span></div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {guest ? (
                    <button onClick={()=>onSignIn?.()} style={{padding:"7px 14px",border:"none",borderRadius:D.radius,background:D.black,color:D.white,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Log in</button>
                  ) : (<>
                    <div className="tap" onClick={()=>{setShowNotifs(true);loadNotifs();}} style={{width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`}}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    </div>
                    <div className="tap" onClick={()=>setUserProfile(true)}><Avatar url={me.avatarUrl} initials={me.avatar} size={30}/></div>
                  </>)}
                </div>
              </div>
            )}
            {!searchActive && (
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                {!campusMode && (
                  <button onClick={()=>{setSliderRadius(radiusFilter||1);setShowRadiusModal(true);}}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"4px 12px",border:`1.5px solid ${D.black}`,borderRadius:D.radiusSm,background:D.black,color:D.white,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"Noto Sans,sans-serif",flexShrink:0}}>
                    <span>📍</span>
                    <span>{radiusFilter===null?"Any distance":radiusFilter<1?`Within ${(radiusFilter*5280)|0}ft`:`Within ${radiusFilter}mi`}</span>
                    <span style={{fontSize:9,marginLeft:2}}>▼</span>
                  </button>
                )}
                <button onClick={()=>setCampusMode(m=>!m)}
                  style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",border:`1.5px solid ${campusMode?D.black:D.gray200}`,borderRadius:D.radiusSm,background:campusMode?D.black:D.white,color:campusMode?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",fontFamily:"Noto Sans,sans-serif",flexShrink:0}}>
                  <span>🎓</span><span>Campus</span>
                </button>
              </div>
            )}
            {!searchActive && (
              <div style={{fontSize:10,color:D.gray600,fontWeight:500,marginBottom:6}}>
                {campusMode ? `Campus sellers near ${cityLabel}` : radiusFilter===null ? `All sellers · ${cityLabel}` : `Within ${radiusFilter<1?`${(radiusFilter*5280)|0}ft`:`${radiusFilter}mi`} · ${cityLabel}`}
              </div>
            )}
            {/* Search input — always visible, expands on focus */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              {searchActive && (
                <div className="tap" onClick={()=>{setSearchActive(false);setSearchQuery("");setSearchSort("relevance");setSearchTab("categories");}} style={{fontSize:18,fontWeight:700,cursor:"pointer",flexShrink:0,padding:"0 4px 0 0"}}>←</div>
              )}
              <div style={{flex:1,display:"flex",alignItems:"center",gap:8,border:`1.5px solid ${searchActive?D.black:D.gray200}`,borderRadius:D.radius,padding:"9px 12px",background:D.white,transition:"border-color .15s"}}>
                <span style={{fontSize:13,color:D.gray400}}>🔍</span>
                <input
                  value={searchQuery}
                  onChange={e=>{setSearchQuery(e.target.value);if(e.target.value.length>0&&searchTab==="categories")setSearchTab("services");}}
                  onFocus={()=>setSearchActive(true)}
                  placeholder="Search services, goods, people..."
                  style={{flex:1,border:"none",fontSize:13,fontWeight:500,color:D.black,background:"none"}}
                />
                {searchQuery.length>0 && (
                  <div className="tap" onClick={()=>setSearchQuery("")} style={{fontSize:11,color:D.gray400,cursor:"pointer",flexShrink:0}}>✕</div>
                )}
              </div>
            </div>
            {/* Mobile landing tabs */}
            {!searchActive && (
              <div style={{display:"flex",margin:"8px -16px 0",borderTop:`1px solid ${D.gray100}`,overflowX:"auto",scrollbarWidth:"none"}}>
                {[["all","Browse all"],["category","Browse by category"],["flash","⚡ Flash"]].map(([k,label])=>(
                  <div key={k} onClick={()=>setDesktopLandingTab(k)} style={{padding:"9px 16px",fontSize:12,fontWeight:800,whiteSpace:"nowrap",flexShrink:0,borderBottom:desktopLandingTab===k?`2.5px solid ${k==="flash"?D.red:D.black}`:"2.5px solid transparent",color:desktopLandingTab===k?(k==="flash"?D.red:D.black):D.gray400,cursor:"pointer"}}>
                    {label}{k==="flash"&&allListings.filter(l=>l.flash).length>0?` (${allListings.filter(l=>l.flash).length})`:""}
                  </div>
                ))}
              </div>
            )}
          </div>}

          {/* ── EXPLORE content (hidden while searching) ── */}
          {!searchActive && (<>
            {/* Mobile-only filter panels — Browse all only */}
            {!isDesktop && desktopLandingTab==="all" && (<>
              <div style={{padding:"10px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
                <div style={{display:"flex",gap:6}}>
                  {[["both","All"],["services","Services"],["goods","Goods"]].map(([k,label]) => (
                    <button key={k} onClick={()=>{setExploreKind(k);setSubCat(new Set());}}
                      style={{flex:1,padding:"7px 0",border:`1.5px solid ${exploreKind===k?D.black:D.gray200}`,borderRadius:D.radius,background:exploreKind===k?D.black:D.white,color:exploreKind===k?D.white:D.gray800,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {(exploreKind==="goods"||exploreKind==="both") && (
                <div style={{padding:"10px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
                  <Label style={{marginBottom:8}}>Fulfillment</Label>
                  <div style={{display:"flex",gap:6}}>
                    {[["pickup","📍 Pickup"],["delivery","🛵 Delivery"],["mail","📬 Mail"]].map(([k,label]) => (
                      <button key={k} onClick={()=>setGoodsFulfill(k)} style={{flex:1,padding:"7px 0",border:`1.5px solid ${goodsFulfill===k?D.black:D.gray200}`,borderRadius:D.radius,background:goodsFulfill===k?D.black:D.white,color:goodsFulfill===k?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>{label}</button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{padding:"10px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
                <Label style={{marginBottom:8}}>{exploreKind==="services"?"Service type":"Category"}</Label>
                <div style={{display:"flex",gap:6,overflowX:"auto"}}>
                  <Chip active={subCat.size===0} onClick={()=>setSubCat(new Set())}>All</Chip>
                  {catOptions.map(c => <Chip key={c} active={subCat.has(c)} onClick={()=>setSubCat(prev=>{const n=new Set(prev);n.has(c)?n.delete(c):n.add(c);return n;})}>{c}</Chip>)}
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white,overflowX:"auto"}}>
                <Label style={{flexShrink:0}}>Sort:</Label>
                {(exploreKind==="goods"?["Closest","Price ↑","Top Rated"]:SORT_OPTS).map(o => (
                  <Chip key={o} active={sort===o} onClick={()=>setSort(o)}>{o}</Chip>
                ))}
              </div>
            </>)}

            {/* Desktop: banners */}
            {isDesktop && BANNERS.length > 0 && (
              <div style={{padding:"24px 32px 0",background:D.white,display:"grid",gridTemplateColumns:`repeat(${BANNERS.length},1fr)`,gap:16}}>
                {BANNERS.map((b,i) => (
                  <div key={i} style={{borderRadius:D.radius,overflow:"hidden",background:b.bg,padding:"28px 32px",display:"flex",flexDirection:"column",justifyContent:"space-between",minHeight:160,position:"relative",cursor:b.ctaUrl||b.cta?"pointer":"default"}}
                    onClick={()=>{if(b.ctaUrl)window.open(b.ctaUrl,"_blank");else if(b.cta)setDesktopLandingTab("all");}}>
                    {b.img && <img src={b.img} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",opacity:0.25}}/>}
                    <div style={{position:"relative"}}>
                      {b.label && <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.1em",color:b.color,opacity:0.7,marginBottom:8}}>{b.label}</div>}
                      <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.03em",color:b.color,lineHeight:1.2,marginBottom:8}}>{b.title}</div>
                      {b.sub && <div style={{fontSize:13,color:b.color,opacity:0.8,lineHeight:1.5}}>{b.sub}</div>}
                    </div>
                    {b.cta && (
                      <div style={{position:"relative",marginTop:20,display:"inline-flex"}}>
                        <div style={{padding:"9px 20px",borderRadius:D.radius,background:b.color,color:b.bg,fontSize:12,fontWeight:800,letterSpacing:"0.02em",display:"inline-block"}}>
                          {b.cta} →
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Desktop: landing tabs */}
            {isDesktop && (
              <div style={{display:"flex",borderBottom:`1px solid ${D.gray200}`,background:D.white,padding:"0 32px",position:"sticky",top:60,zIndex:9}}>
                {[["all","Browse all"],["category","Browse by category"],["flash","⚡ Flash"]].map(([k,label])=>(
                  <div key={k} onClick={()=>setDesktopLandingTab(k)} style={{padding:"14px 0",marginRight:32,fontSize:13,fontWeight:800,letterSpacing:"-0.01em",borderBottom:desktopLandingTab===k?`2.5px solid ${k==="flash"?D.red:D.black}`:"2.5px solid transparent",color:desktopLandingTab===k?(k==="flash"?D.red:D.black):D.gray400,cursor:"pointer",whiteSpace:"nowrap"}}>
                    {label}{k==="flash"&&allListings.filter(l=>l.flash).length>0?` (${allListings.filter(l=>l.flash).length})`:""}
                  </div>
                ))}
              </div>
            )}

            {/* Desktop: active filter chips (Browse all tab only) */}
            {isDesktop && desktopLandingTab==="all" && (subCat.size>0||exploreKind!=="both") && (
              <div style={{padding:"12px 32px 10px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",borderBottom:`1px solid ${D.gray100}`,background:D.white}}>
                {subCat.size>0 && [...subCat].map(c=>(
                  <div key={c} className="tap" onClick={()=>setSubCat(prev=>{const n=new Set(prev);n.delete(c);return n;})} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:24,background:D.black,color:D.white,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    {c} <span style={{fontSize:14,lineHeight:1}}>×</span>
                  </div>
                ))}
                {exploreKind!=="both" && (
                  <div className="tap" onClick={()=>setExploreKind("both")} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:24,background:D.black,color:D.white,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    {exploreKind==="services"?"Services":"Goods"} <span style={{fontSize:14,lineHeight:1}}>×</span>
                  </div>
                )}
                <span style={{fontSize:12,color:D.gray500,marginLeft:4}}>{filtered.length} listing{filtered.length!==1?"s":""}</span>
              </div>
            )}

            {/* Browse by category tab content */}
            {desktopLandingTab==="category" && (
              <div style={{padding: isDesktop ? "32px 32px 40px" : "12px 12px 100px",background:D.white}}>
                <div style={{display:"grid",gridTemplateColumns: isDesktop ? "repeat(6,1fr)" : "1fr 1fr",gap: isDesktop ? 12 : 10}}>
                  {CORE_CATS.map((cat,i)=>{
                    const img = catCoverImg[cat];
                    return (
                      <div key={cat} className="tap" onClick={()=>{setSubCat(new Set([cat]));setDesktopLandingTab("all");}} style={{borderRadius:D.radius,overflow:"hidden",cursor:"pointer",border:`1px solid ${D.gray100}`,transition:"box-shadow .15s",position:"relative"}}
                        onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.12)"}
                        onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                        <div style={{aspectRatio: isDesktop ? "1/1" : "3/2",background:img?"#000":CAT_BG[i%CAT_BG.length],display:"flex",alignItems:"center",justifyContent:"center",fontSize: isDesktop ? 36 : 28,position:"relative",overflow:"hidden"}}>
                          {img
                            ? <img src={img} alt={cat} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                            : CAT_ICONS[cat]||"🏷️"}
                        </div>
                        <div style={{padding: isDesktop ? "8px 6px" : "8px 10px",fontSize: isDesktop ? 11 : 12,fontWeight:700,textAlign: isDesktop ? "center" : "left",color:D.black,lineHeight:1.3,background:D.white}}>{cat}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mobile count */}
            {!isDesktop && desktopLandingTab==="all" && (
              <div style={{padding:"8px 16px",background:D.gray50}}>
                <span style={{fontSize:11,color:D.gray600,fontWeight:600}}>{filtered.length} {exploreKind==="services"?"services":exploreKind==="goods"?"items":"listings"}{subCat.size>0?` · ${[...subCat].join(", ")}`:""}</span>
              </div>
            )}

            {/* Card grid: desktop Browse all tab, or mobile */}
            {(desktopLandingTab==="all" || desktopLandingTab==="flash") && (() => {
              const isFlashTab = desktopLandingTab==="flash";
              const cards = isFlashTab ? allListings.filter(l=>l.flash).sort((a,b)=>a.slot-b.slot) : filtered;
              return (
                <div className="g2" style={{display:"grid",gridTemplateColumns: isDesktop ? "repeat(auto-fill,minmax(220px,1fr))" : "1fr 1fr",gap: isDesktop ? 1 : 10,padding: isDesktop ? "0" : "0 12px 100px",background: isDesktop ? D.gray100 : undefined}}>
                  {cards.map(l => <Card key={l.lid} l={l}/>)}
                  {cards.length===0 && (
                    <div style={{gridColumn:"1/-1",padding:"48px 0",textAlign:"center",color:D.gray400,background:D.white}}>
                      <div style={{fontSize:32,marginBottom:10}}>{isFlashTab?"⚡":"🔍"}</div>
                      <div style={{fontSize:14,fontWeight:700,color:D.gray800,marginBottom:4}}>{isFlashTab?"No flash listings right now":"Nothing here yet"}</div>
                      <div style={{fontSize:12}}>{isFlashTab?"Check back soon — sellers go flash when they're available now":"Try a different category or check back soon"}</div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>)}

          {/* ── SEARCH RESULTS ── */}
          {searchActive && (() => {
            const curSvc   = sortedSvcResults;
            const curGoods = sortedGoodsResults;
            const curUsers = sortedUserResults;
            const noQuery  = sq.length < 2;
            const sortOpts = [
              {k:"relevance",l:"Relevance"},
              {k:"distance", l:"Closest"},
              {k:"rating",   l:"Top Rated"},
              ...(searchTab!=="users"?[{k:"price_asc",l:"Price ↑"},{k:"price_desc",l:"Price ↓"}]:[]),
            ];
            return (
              <>
                {/* Result type tabs */}
                <div style={{display:"flex",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
                  {[["categories","Categories",0],["services","Services",curSvc.length],["goods","Goods",curGoods.length],["users","People",curUsers.length]].map(([key,label,cnt],i) => (
                    <div key={key} onClick={()=>setSearchTab(key)}
                      style={{flex:1,textAlign:"center",padding:"11px 0",fontSize:11,fontWeight:800,letterSpacing:"0.02em",borderBottom:searchTab===key?`2.5px solid ${D.black}`:"2.5px solid transparent",color:searchTab===key?D.black:D.gray400,cursor:"pointer",borderRight:i<3?`1px solid ${D.gray200}`:"none"}}>
                      {label}{key!=="categories"&&!noQuery&&cnt>0?` (${cnt})`:""}
                    </div>
                  ))}
                </div>

                {/* Sort strip — hidden on categories tab */}
                {searchTab!=="categories" && (
                  <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white,overflowX:"auto"}}>
                    <Label style={{flexShrink:0}}>Sort:</Label>
                    {sortOpts.map(o => <Chip key={o.k} active={searchSort===o.k} onClick={()=>setSearchSort(o.k)}>{o.l}</Chip>)}
                  </div>
                )}

                {/* Categories tab */}
                {searchTab==="categories" && (
                  <div style={{padding:"12px 12px 100px",background:D.gray50}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                      {CORE_CATS.map((cat,i) => {
                        const img = catCoverImg[cat];
                        return (
                          <div key={cat} className="tap" onClick={()=>{setSubCat(new Set([cat]));setSearchActive(false);setSearchQuery("");}}
                            style={{borderRadius:D.radius,overflow:"hidden",cursor:"pointer",border:`1px solid ${D.gray100}`,background:D.white}}>
                            <div style={{aspectRatio:"3/2",background:img?"#000":CAT_BG[i%CAT_BG.length],display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,position:"relative",overflow:"hidden"}}>
                              {img
                                ? <img src={img} alt={cat} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                                : CAT_ICONS[cat]||"🏷️"}
                            </div>
                            <div style={{padding:"8px 10px",fontSize:12,fontWeight:800,color:D.black}}>{cat}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty prompt */}
                {noQuery && searchTab!=="categories" && (
                  <div style={{padding:"60px 24px",textAlign:"center",color:D.gray400}}>
                    <div style={{fontSize:36,marginBottom:12}}>🔍</div>
                    <div style={{fontSize:15,fontWeight:800,color:D.gray800,marginBottom:6}}>Search nearme.</div>
                    <div style={{fontSize:12,lineHeight:1.5}}>Find services, goods, and people near you</div>
                  </div>
                )}

                {/* Services results */}
                {!noQuery && searchTab==="services" && (
                  curSvc.length===0
                    ? <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400}}><div style={{fontSize:28,marginBottom:10}}>😶</div><div style={{fontSize:14,fontWeight:700,color:D.gray800,marginBottom:4}}>No services found</div><div style={{fontSize:12}}>Try a different keyword</div></div>
                    : <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,padding:"10px 12px 100px"}}>{curSvc.map(l=><Card key={l.lid} l={l}/>)}</div>
                )}

                {/* Goods results */}
                {!noQuery && searchTab==="goods" && (
                  curGoods.length===0
                    ? <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400}}><div style={{fontSize:28,marginBottom:10}}>😶</div><div style={{fontSize:14,fontWeight:700,color:D.gray800,marginBottom:4}}>No goods found</div><div style={{fontSize:12}}>Try a different keyword</div></div>
                    : <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,padding:"10px 12px 100px"}}>{curGoods.map(l=><Card key={l.lid} l={l}/>)}</div>
                )}

                {/* People results */}
                {!noQuery && searchTab==="users" && (
                  curUsers.length===0
                    ? <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400}}><div style={{fontSize:28,marginBottom:10}}>😶</div><div style={{fontSize:14,fontWeight:700,color:D.gray800,marginBottom:4}}>No people found</div><div style={{fontSize:12}}>Try searching a name, skill, or handle</div></div>
                    : <div style={{paddingBottom:100}}>
                        {curUsers.map(s => (
                          <div key={s.id} className="tap hvr" onClick={()=>openSeller(s)}
                            style={{display:"flex",gap:12,padding:"13px 16px",borderBottom:`1px solid ${D.gray100}`,background:D.white,cursor:"pointer",alignItems:"center"}}>
                            <Avatar url={s.avatarUrl} initials={s.avatar} size={46}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}>
                                <span style={{fontSize:14,fontWeight:800,letterSpacing:"-0.01em"}}>{s.name}</span>
                                {s.verified && <span style={{fontSize:9,fontWeight:800,background:D.black,color:D.white,padding:"1px 4px",borderRadius:2}}>✓</span>}
                                {s.promoted && <span style={{fontSize:9,fontWeight:800,background:D.red,color:D.white,padding:"1px 4px",borderRadius:2}}>PRO</span>}
                              </div>
                              <div style={{fontSize:11,color:D.gray600,fontWeight:600,marginBottom:3}}>{s.handle} · {s.trade}</div>
                              <div style={{fontSize:11,color:D.gray800,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.bio}</div>
                              <div style={{display:"flex",gap:10,marginTop:5}}>
                                <span style={{fontSize:10,fontWeight:700,color:D.gray600}}>★ {s.rating}</span>
                                <span style={{fontSize:10,color:D.gray400,fontWeight:600}}>📍 {s.distance}mi</span>
                                <span style={{border:`1px solid ${D.gray200}`,borderRadius:3,padding:"1px 5px",fontSize:10,fontWeight:600,color:D.gray800}}>{s.tag}</span>
                              </div>
                            </div>
                            <span style={{fontSize:14,color:D.gray400,flexShrink:0}}>›</span>
                          </div>
                        ))}
                      </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ══ FEED ══════════════════════════════ */}
      {tab==="feed" && !sellerView && !userProfile && (
        <div className="fade">
          <AppHeader title="Feed" right={null}/>
          <div style={isDesktop ? {maxWidth:640,margin:"0 auto"} : {}}>
            {/* Feed tabs */}
            <div style={{display:"flex",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
              {[["following","Following"],["foryou","For You"]].map(([key,label]) => (
                <div key={key} className="tap" onClick={()=>setFeedTab(key)} style={{flex:1,textAlign:"center",padding:"10px 0",fontSize:12,fontWeight:800,letterSpacing:"0.03em",borderBottom:feedTab===key?`2.5px solid ${D.black}`:"2.5px solid transparent",color:feedTab===key?D.black:D.gray400,cursor:"pointer",borderRight:key==="following"?`1px solid ${D.gray200}`:"none"}}>
                  {label}
                </div>
              ))}
            </div>

            <div style={{paddingBottom:90}}>
              {feedTab==="following" && (
                allListings.filter(l=>following.has(l.seller.id)).length===0
                  ? <div style={{padding:"60px 24px",textAlign:"center",color:D.gray400}}><div style={{fontSize:32,marginBottom:12}}>👋</div><div style={{fontSize:14,fontWeight:800,color:D.gray800,marginBottom:6}}>Follow sellers to see their posts</div><div style={{fontSize:12}}>Head to Explore</div></div>
                  : allListings.filter(l=>following.has(l.seller.id)).map(l => { const p={...l.seller.posts[0],seller:l.seller,price:l.price,flash:l.flash,slot:l.slot}; return <Post key={`fl-${l.lid}`} post={p}/>; })
              )}
              {feedTab==="foryou" && (<>
                <div style={{padding:"8px 16px",borderBottom:`1px solid ${D.gray100}`}}><span style={{fontSize:11,color:D.gray600,fontWeight:600}}>Based on: {me.interests.join(", ")}</span></div>
                {forYou.filter(l=>l.seller.posts[0]).map(l => { const p={...l.seller.posts[0],seller:l.seller,price:l.price,flash:l.flash,slot:l.slot}; return <Post key={`fy-${l.lid}`} post={p}/>; })}
              </>)}
            </div>
          </div>
        </div>
      )}

      {/* ══ FLASH ════════════════════════════ */}
      {tab==="flash" && !sellerView && !userProfile && (
        <div className="fade">
          <AppHeader title="Flash" right={<span style={{fontSize:10,fontWeight:700,color:D.gray600,letterSpacing:"0.06em"}}>AVAILABLE NOW</span>}/>
          <div style={{paddingBottom:90}}>
            {allListings.filter(l=>l.flash).sort((a,b)=>a.slot-b.slot).map(l => (
              <div key={l.lid} className="tap" onClick={()=>openListing(l)} style={{display:"flex",gap:0,borderBottom:`1px solid ${D.gray100}`,background:D.white,cursor:"pointer"}}>
                <div style={{width:84,height:84,background:D.gray100,flexShrink:0,overflow:"hidden"}}>
                  <img src={l.seller.posts[0]?.img} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                </div>
                <div style={{flex:1,padding:"10px 14px",display:"flex",flexDirection:"column",justifyContent:"center",gap:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <div className="tap" onClick={e=>{e.stopPropagation();openSeller(l.seller);}}><Avatar url={l.seller.avatarUrl} initials={l.seller.avatar} size={16} radius={3}/></div>
                    <span style={{fontSize:10,color:D.gray600,fontWeight:600}}>{l.seller.handle}</span>
                  </div>
                  <div style={{fontSize:13,fontWeight:800,letterSpacing:"-0.01em"}}>{l.name}</div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:6,height:6,borderRadius:"50%",background:slotColor(l.slot)}}/><span style={{fontSize:11,fontWeight:700,color:slotColor(l.slot)}}>{slotLabel(l.slot)}</span></div>
                    <span style={{fontSize:10,color:D.gray400}}>📍 {l.seller.distance}mi · {l.seller.location}</span>
                  </div>
                </div>
                <div style={{padding:"10px 14px",display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"flex-end",borderLeft:`1px solid ${D.gray100}`}}>
                  <div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.02em"}}>${l.price}</div>
                  <div style={{fontSize:10,color:D.gray400,marginTop:2}}>★ {l.seller.rating}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ SELLER PROFILE ═══════════════════ */}
      {sellerView && !userProfile && (() => {
        const ListingsGrid = ({cols}) => {
          const items = sellerView.isReal ? sellerListings : sellerView.posts;
          if (sellerListingsLoading) return <div style={{padding:40,textAlign:"center",color:D.gray400,fontSize:13}}>Loading…</div>;
          if (!items.length) return <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No listings yet</div>;
          return (
            <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:2,background:D.gray100}}>
              {(sellerView.isReal ? sellerListings.map(l=>({id:l.id,img:l.image_urls?.[0],price:l.price,flash:l.is_flash,kind:l.kind,name:l.name,lid:l.id,slot:l.slot_minutes??0,description:l.description||"",image_urls:l.image_urls||[]})) : sellerView.posts).map((l,i) => (
                <div key={l.id||i} className="tap" onClick={()=>sellerView.isReal?openListing({name:l.name,price:l.price,slot:l.slot??0,flash:l.flash||l.is_flash,kind:l.kind,seller:sellerView,lid:l.lid||l.id,image_urls:l.image_urls||[],description:l.description||""}):null} style={{aspectRatio:"1/1",background:D.white,overflow:"hidden",position:"relative",cursor:sellerView.isReal?"pointer":"default"}}>
                  {(l.img||l.image_urls?.[0])
                    ? <img src={l.img||l.image_urls[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                    : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,color:D.gray300}}>{l.kind==="product"?"📦":"🛠️"}</div>}
                  <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"18px 6px 5px",background:"linear-gradient(transparent,rgba(0,0,0,0.55))"}}>
                    <span style={{fontSize:11,fontWeight:900,color:D.white}}>${l.price}</span>
                  </div>
                  {(l.flash||l.is_flash) && <div style={{position:"absolute",top:5,left:5,background:D.red,borderRadius:3,padding:"2px 5px"}}><span style={{fontSize:8,fontWeight:900,color:D.white,letterSpacing:"0.06em"}}>FLASH</span></div>}
                </div>
              ))}
            </div>
          );
        };
        const ShopList = () => {
          const allItems = sellerView.isReal ? sellerListings : sellerView.services;
          if (sellerListingsLoading) return <div style={{padding:40,textAlign:"center",color:D.gray400,fontSize:13}}>Loading…</div>;
          if (!allItems.length) return <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No listings yet</div>;
          return (
            <div>
              {["service","product"].map(kind => {
                const items = allItems.filter(l => (l.kind||l.type)===kind);
                if (!items.length) return null;
                return (
                  <div key={kind}>
                    <div style={{padding:"8px 16px",background:D.gray50,borderBottom:`1px solid ${D.gray100}`}}>
                      <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,textTransform:"uppercase"}}>{kind==="service"?"Services":"Goods & Products"}</span>
                    </div>
                    {items.map((l,i) => (
                      <div key={l.id||i} className="tap hvr" onClick={()=>openListing({name:l.name,price:l.price,slot:l.slot_minutes??l.slot??0,flash:l.is_flash||l.flash,kind:l.kind||l.type,seller:sellerView,lid:l.id||`${sellerView.id}-svc-${i}`,image_urls:l.image_urls||[],description:l.description||""})} style={{display:"flex",alignItems:"center",padding:"14px 16px",borderBottom:`1px solid ${D.gray100}`,cursor:"pointer",background:D.white,gap:12}}>
                        <div style={{width:52,height:52,background:D.gray100,borderRadius:D.radiusSm,overflow:"hidden",flexShrink:0}}>
                          {l.image_urls?.[0] ? <img src={l.image_urls[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{kind==="product"?"📦":"🛠️"}</div>}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:700,letterSpacing:"-0.01em",marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div>
                          {kind==="service"
                            ? <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:6,height:6,borderRadius:"50%",background:slotColor(l.slot_minutes??l.slot??0)}}/><span style={{fontSize:11,fontWeight:700,color:slotColor(l.slot_minutes??l.slot??0)}}>{slotLabel(l.slot_minutes??l.slot??0)}</span></div>
                            : <span style={{fontSize:11,color:D.gray500}}>In stock</span>}
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.02em"}}>${l.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        };
        const ProfileInfo = ({center}) => (
          <div style={{textAlign:center?"center":"left"}}>
            <div style={{display:"flex",justifyContent:center?"center":"flex-start",marginBottom:12}}>
              <div style={{position:"relative",display:"inline-block"}}>
                <Avatar url={sellerView.avatarUrl} initials={sellerView.avatar} size={center?88:80} radius={99}/>
                {sellerView.promoted && <div style={{position:"absolute",bottom:0,right:0,width:20,height:20,background:D.red,borderRadius:"50%",border:`2px solid ${D.white}`,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:8,fontWeight:900,color:D.white}}>P</span></div>}
              </div>
            </div>
            <div style={{fontSize:center?20:18,fontWeight:900,letterSpacing:"-0.03em",marginBottom:2}}>{sellerView.name}</div>
            <div style={{fontSize:13,color:D.gray500,fontWeight:500,marginBottom:center?6:8}}>{sellerView.handle}</div>
            {sellerView.bio && <div style={{fontSize:13,color:D.gray700,lineHeight:1.5,marginBottom:6,maxWidth:center?280:"100%",margin:center?"0 auto 6px":"0 0 6px"}}>{sellerView.bio}</div>}
            {sellerView.location && <div style={{fontSize:12,color:D.gray500,marginBottom:12,textAlign:center?"center":"left"}}>📍 {sellerView.location}</div>}
            {/* Stats */}
            <div style={{display:"flex",gap:center?24:16,justifyContent:center?"center":"flex-start",marginBottom:16}}>
              {[[sellerView.reviews||0,"Reviews"],[sellerView.followers||0,"Followers"],[sellerView.rating||"New","Rating"]].map(([val,label]) => (
                <div key={label} style={{textAlign:"center"}}>
                  <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.02em"}}>{val}</div>
                  <div style={{fontSize:10,color:D.gray500,fontWeight:500}}>{label}</div>
                </div>
              ))}
            </div>
            {/* Buttons */}
            <div style={{display:"flex",gap:8,justifyContent:center?"center":"flex-start",marginBottom:14}}>
              <button className="tap" onClick={()=>requireAuth(()=>toggleFollow(sellerView.id))} style={{padding:"9px 24px",borderRadius:24,border:`1.5px solid ${following.has(sellerView.id)?D.gray200:D.black}`,background:following.has(sellerView.id)?D.white:D.black,color:following.has(sellerView.id)?D.black:D.white,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",minWidth:100}}>
                {following.has(sellerView.id)?"Following":"Follow"}
              </button>
              <button className="tap" onClick={()=>requireAuth(()=>{setMsgSent(false);setMsgText("");setShowMsgCompose(true);})} style={{padding:"9px 24px",borderRadius:24,border:`1.5px solid ${D.gray200}`,background:D.white,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Message</button>
            </div>
            {/* Tags */}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:center?"center":"flex-start"}}>
              {[sellerView.trade,sellerView.location&&`📍 ${sellerView.location}`,sellerView.campus&&`🎓 ${sellerView.campus}`].filter(Boolean).map(t=>(
                <span key={t} style={{border:`1px solid ${D.gray100}`,borderRadius:20,padding:"3px 10px",fontSize:11,color:D.gray600,background:D.gray50}}>{t}</span>
              ))}
            </div>
          </div>
        );
        const Tabs = () => (
          <div style={{display:"flex",borderBottom:`1px solid ${D.gray100}`,background:D.white}}>
            {[["posts","Shop"],["clients","Reviews"],["likes","Likes"]].map(([key,label]) => (
              <div key={key} className="tap" onClick={()=>setProfileTab(key)} style={{flex:1,textAlign:"center",padding:"13px 0",fontSize:12,fontWeight:800,letterSpacing:"0.04em",borderBottom:profileTab===key?`2px solid ${D.black}`:"2px solid transparent",color:profileTab===key?D.black:D.gray400,cursor:"pointer"}}>
                {label}
              </div>
            ))}
          </div>
        );
        return (
          <div className="fade" style={{background:D.white,minHeight:"100vh"}}>
            {/* Top bar */}
            <div style={{position:"sticky",top:isDesktop?56:0,zIndex:20,background:D.white,borderBottom:`1px solid ${D.gray100}`,display:"flex",alignItems:"center",gap:12,padding:"13px 16px"}}>
              <button onClick={()=>closeSeller()} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:D.black,padding:0,lineHeight:1,flexShrink:0}}>←</button>
              <span style={{fontSize:14,fontWeight:700,color:D.gray600}}>{sellerView.handle}</span>
              {sellerView.verified && <span style={{marginLeft:"auto",fontSize:9,fontWeight:800,background:D.black,color:D.white,padding:"2px 6px",borderRadius:3}}>✓ Verified</span>}
            </div>

            {isDesktop ? (
              <div style={{maxWidth:1060,margin:"0 auto",padding:"32px 32px",display:"grid",gridTemplateColumns:"280px 1fr",gap:48,alignItems:"start"}}>
                <div style={{position:"sticky",top:100}}>
                  <ProfileInfo center={false}/>
                </div>
                <div>
                  <Tabs/>
                  {profileTab==="posts" && <ListingsGrid cols={3}/>}
                  {profileTab==="clients" && (
                    <div style={{padding:"16px 0"}}>
                      {sellerView.publicClients.length===0
                        ? <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No reviews yet</div>
                        : sellerView.publicClients.map((c,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${D.gray100}`}}>
                            <Avatar initials={c[0]?.toUpperCase()||"?"} size={32} radius={99}/>
                            <span style={{fontSize:13,fontWeight:700}}>{c}</span>
                            <span style={{marginLeft:"auto",fontSize:11,color:D.gray400}}>Repeat client</span>
                          </div>
                        ))}
                    </div>
                  )}
                  {profileTab==="likes" && <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No liked items yet</div>}
                </div>
              </div>
            ) : (
              <div style={{paddingBottom:80}}>
                <div style={{padding:"24px 16px 20px",borderBottom:`1px solid ${D.gray100}`}}>
                  <ProfileInfo center={true}/>
                </div>
                <div style={{position:"sticky",top:48,zIndex:10,background:D.white}}>
                  <Tabs/>
                </div>
                {profileTab==="posts" && <ListingsGrid cols={2}/>}
                {profileTab==="clients" && (
                  <div style={{padding:"0 16px"}}>
                    {sellerView.publicClients.length===0
                      ? <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No reviews yet</div>
                      : sellerView.publicClients.map((c,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 0",borderBottom:`1px solid ${D.gray100}`}}>
                          <Avatar initials={c[0]?.toUpperCase()||"?"} size={32} radius={99}/>
                          <span style={{fontSize:13,fontWeight:700}}>{c}</span>
                          <span style={{marginLeft:"auto",fontSize:11,color:D.gray400}}>Repeat client</span>
                        </div>
                      ))}
                  </div>
                )}
                {profileTab==="likes" && <div style={{padding:"48px 24px",textAlign:"center",color:D.gray400,fontSize:13}}>No liked items yet</div>}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ USER PROFILE ═════════════════════ */}
      {userProfile && (
        <div className="fade" style={{paddingBottom:90,background:D.gray50}}>
          <div className="tap" onClick={()=>setUserProfile(false)} style={{padding:"12px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white,display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <span style={{fontSize:16,fontWeight:700}}>←</span>
          </div>
          <div style={{padding:"16px",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
              <div className="tap" onClick={()=>avatarInputRef.current?.click()} style={{position:"relative",flexShrink:0}}>
                <Avatar url={me.avatarUrl} initials={me.avatar} size={60}/>
                <div style={{position:"absolute",bottom:-4,right:-4,width:18,height:18,borderRadius:"50%",background:D.black,border:`2px solid ${D.white}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:9,color:D.white,lineHeight:1}}>✎</span>
                </div>
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleAvatarChange}/>
              <div>
                <div style={{fontSize:18,fontWeight:900,letterSpacing:"-0.02em"}}>{me.name}</div>
                <div style={{fontSize:12,color:D.gray600,fontWeight:600,marginBottom:6}}>{me.handle}</div>
                {/* Bio */}
                {editingBio
                  ? <div style={{marginBottom:6}}>
                      <textarea value={bioDraft} onChange={e=>setBioDraft(e.target.value)} autoFocus rows={3} maxLength={150} style={{width:"100%",fontSize:12,padding:"6px 8px",border:`1.5px solid ${D.black}`,borderRadius:D.radiusSm,fontFamily:"Noto Sans,sans-serif",resize:"none",lineHeight:1.5}}/>
                      <div style={{display:"flex",gap:6,marginTop:4}}>
                        <button onClick={async()=>{setMe(m=>({...m,bio:bioDraft}));setEditingBio(false);try{await updateProfile({name:me.name,handle:me.handle,bio:bioDraft,interests:me.interests,avatar_url:me.avatarUrl});}catch{}}} style={{flex:1,padding:"6px",borderRadius:D.radiusSm,border:"none",background:D.black,color:D.white,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Save</button>
                        <button onClick={()=>setEditingBio(false)} style={{padding:"6px 12px",borderRadius:D.radiusSm,border:`1px solid ${D.gray200}`,background:D.white,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Cancel</button>
                      </div>
                    </div>
                  : <div className="tap" onClick={()=>{setBioDraft(me.bio);setEditingBio(true);}} style={{fontSize:12,color:me.bio?D.gray800:D.gray400,marginBottom:6,cursor:"pointer",lineHeight:1.5}}>
                      {me.bio || "Add a bio…"}
                    </div>
                }
                {/* Location */}
                {editingLocation
                  ? <div>
                      <input value={locationDraft} onChange={e=>setLocationDraft(e.target.value)} autoFocus placeholder="e.g. West Chester, PA" maxLength={60} style={{width:"100%",fontSize:12,padding:"6px 8px",border:`1.5px solid ${D.black}`,borderRadius:D.radiusSm,fontFamily:"Noto Sans,sans-serif"}}/>
                      <div style={{display:"flex",gap:6,marginTop:4}}>
                        <button onClick={async()=>{setMe(m=>({...m,locationText:locationDraft}));setEditingLocation(false);try{await updateProfile({name:me.name,handle:me.handle,bio:me.bio,interests:me.interests,avatar_url:me.avatarUrl,location_text:locationDraft});}catch{}}} style={{flex:1,padding:"6px",borderRadius:D.radiusSm,border:"none",background:D.black,color:D.white,fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Save</button>
                        <button onClick={()=>setEditingLocation(false)} style={{padding:"6px 12px",borderRadius:D.radiusSm,border:`1px solid ${D.gray200}`,background:D.white,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>Cancel</button>
                      </div>
                    </div>
                  : <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div className="tap" onClick={()=>{setLocationDraft(me.locationText);setEditingLocation(true);}} style={{fontSize:12,color:me.locationText?D.gray700:D.gray400,cursor:"pointer"}}>
                        {me.locationText ? `📍 ${me.locationText}` : "Add location…"}
                      </div>
                      {me.locationText && (
                        <button onClick={async()=>{const next=!me.showLocation;setMe(m=>({...m,showLocation:next}));try{await updateProfile({name:me.name,handle:me.handle,bio:me.bio,interests:me.interests,avatar_url:me.avatarUrl,show_location:next});}catch{}}} style={{fontSize:10,padding:"2px 8px",borderRadius:20,border:`1px solid ${D.gray200}`,background:me.showLocation?D.black:D.white,color:me.showLocation?D.white:D.gray600,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                          {me.showLocation?"Shown":"Hidden"}
                        </button>
                      )}
                    </div>
                }
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",border:`1px solid ${D.gray200}`,borderRadius:D.radius,overflow:"hidden",marginBottom:14}}>
              {[["Following",me.following.length],["Visits",me.visitLog.length],["Saved",saved.size]].map(([label,val],i) => (
                <div key={label} style={{padding:"10px",textAlign:"center",borderRight:i<2?`1px solid ${D.gray100}`:"none"}}>
                  <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em"}}>{val}</div>
                  <div style={{fontSize:9,color:D.gray400,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginTop:2}}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Profile / Listings tabs */}
          <div style={{display:"flex",borderBottom:`1px solid ${D.gray200}`,background:D.white}}>
            {[["profile","Profile"],["listings","Listings"]].map(([key,label],i) => (
              <div key={key} className="tap" onClick={()=>setMyProfileTab(key)} style={{flex:1,textAlign:"center",padding:"10px 0",fontSize:11,fontWeight:800,letterSpacing:"0.05em",borderBottom:myProfileTab===key?`2.5px solid ${D.black}`:"2.5px solid transparent",color:myProfileTab===key?D.black:D.gray400,cursor:"pointer",borderRight:i===0?`1px solid ${D.gray100}`:"none"}}>{label}</div>
            ))}
          </div>

          {myProfileTab==="profile" && (<>
            <div style={{padding:"14px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white,marginTop:8}}>
              <Label style={{marginBottom:8}}>My Interests</Label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {me.interests.map(i => <span key={i} style={{border:`1px solid ${D.black}`,borderRadius:D.radiusSm,padding:"4px 10px",fontSize:12,fontWeight:700}}>{i}</span>)}
                <span className="tap" style={{border:`1.5px dashed ${D.gray200}`,borderRadius:D.radiusSm,padding:"4px 10px",fontSize:12,fontWeight:700,color:D.gray400,cursor:"pointer"}}>+ Add</span>
              </div>
            </div>

            {/* Top choices */}
            <div style={{padding:"14px 16px",borderBottom:`1px solid ${D.gray200}`,background:D.white,marginTop:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:900,letterSpacing:"-0.01em"}}>My Top Choices</div>
                <Label>Public</Label>
              </div>
              {me.topChoices.map((tc,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${D.gray100}`}}>
                  <div style={{width:30,height:30,background:D.black,borderRadius:D.radius,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:900,color:D.white}}>{tc.trade[0]}</div>
                  <div><div style={{fontSize:13,fontWeight:800}}>{tc.trade}</div><div style={{fontSize:11,color:D.gray600}}>{tc.seller} · {tc.handle}</div></div>
                  <span style={{marginLeft:"auto"}}>🏆</span>
                </div>
              ))}
              {me.topChoices.length===0 && <div style={{fontSize:12,color:D.gray400,fontWeight:500,padding:"6px 0"}}>None yet</div>}
            </div>

            {/* Visit log */}
            <div style={{padding:"14px 16px",background:D.white,marginTop:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:900,letterSpacing:"-0.01em"}}>Visit Log</div>
                <Label>Optional Public</Label>
              </div>
              {me.visitLog.map((v,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${D.gray100}`}}>
                  <div style={{fontSize:18}}>✅</div>
                  <div><div style={{fontSize:13,fontWeight:800}}>{v.service}</div><div style={{fontSize:11,color:D.gray600}}>{v.seller} · {v.date}</div></div>
                  <div style={{marginLeft:"auto",fontSize:10,color:D.gray400,fontWeight:700,cursor:"pointer"}}>★ Rate</div>
                </div>
              ))}
              {me.visitLog.length===0 && <div style={{fontSize:12,color:D.gray400,fontWeight:500,padding:"6px 0"}}>No visits yet</div>}
            </div>
          </>)}

          {myProfileTab==="listings" && (
            <div style={{background:D.white,marginTop:8}}>
              {myListings===null && (
                <div style={{padding:"40px",textAlign:"center",color:D.gray400,fontSize:13,fontWeight:600}}>Loading…</div>
              )}
              {myListings!==null && myListings.length===0 && (
                <div style={{padding:"40px 24px",textAlign:"center"}}>
                  <div style={{fontSize:28,marginBottom:10}}>🛍️</div>
                  <div style={{fontSize:14,fontWeight:800,marginBottom:6}}>No listings yet</div>
                  <div style={{fontSize:12,color:D.gray400,fontWeight:500}}>Head to the + tab to post your first listing.</div>
                  <button className="tap" onClick={()=>{setUserProfile(false);setTab("seller");}}
                    style={{marginTop:16,padding:"12px 24px",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                    + Post a Listing
                  </button>
                </div>
              )}
              {myListings!==null && myListings.length>0 && (
                <div>
                  {/* header row */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 16px",background:D.gray50,borderBottom:`1px solid ${D.gray100}`}}>
                    <span style={{fontSize:10,fontWeight:700,color:D.gray600,letterSpacing:"0.06em"}}>
                      {selectMode && selectedIds.size>0 ? `${selectedIds.size} SELECTED` : `${myListings.length} LISTING${myListings.length!==1?"S":""}`}
                    </span>
                    <button onClick={()=>{setSelectMode(s=>!s);setSelectedIds(new Set());setDeletingId(null);}}
                      style={{padding:"4px 12px",borderRadius:D.radiusSm,border:`1px solid ${selectMode?D.black:D.gray200}`,background:selectMode?D.black:D.white,color:selectMode?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                      {selectMode?"Cancel":"Select"}
                    </button>
                  </div>

                  {myListings.map(l => {
                    const checked = selectedIds.has(l.id);
                    return (
                      <div key={l.id} style={{borderBottom:`1px solid ${D.gray100}`,background:checked?"#F5F5F5":D.white}}>
                        <div className="tap" onClick={()=>selectMode ? toggleSelectId(l.id) : (setDeletingId(null),openListing(myListingToSelected(l)))}
                          style={{display:"flex",gap:12,padding:"12px 16px",cursor:"pointer",alignItems:"center"}}>
                          {/* checkbox in select mode */}
                          {selectMode && (
                            <div style={{width:22,height:22,borderRadius:4,border:`2px solid ${checked?D.black:D.gray200}`,background:checked?D.black:D.white,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {checked && <span style={{color:D.white,fontSize:12,fontWeight:900,lineHeight:1}}>✓</span>}
                            </div>
                          )}
                          <div style={{width:54,height:54,borderRadius:D.radiusSm,background:D.gray100,flexShrink:0,overflow:"hidden"}}>
                            {l.image_urls?.[0]
                              ? <img src={l.image_urls[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                              : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{l.kind==="product"?"📦":"🛠️"}</div>
                            }
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:800,letterSpacing:"-0.01em",marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</div>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:11,color:D.gray600,fontWeight:500}}>{l.category}</span>
                              <span style={{fontSize:9,fontWeight:800,background:l.kind==="product"?D.black:"#444",color:D.white,padding:"1px 5px",borderRadius:2}}>{l.kind==="product"?"GOODS":"SERVICE"}</span>
                              <span style={{fontSize:9,fontWeight:700,color:l.is_active?D.green:D.gray400}}>● {l.is_active?"Active":"Inactive"}</span>
                            </div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                            <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.02em"}}>${l.price}</div>
                            {!selectMode && (
                              <button onClick={e=>{e.stopPropagation();setDeletingId(id=>id===l.id?null:l.id);}}
                                style={{width:32,height:32,borderRadius:D.radiusSm,border:`1px solid ${D.gray200}`,background:D.white,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                        {!selectMode && deletingId===l.id && (
                          <div style={{display:"flex",gap:8,padding:"0 16px 12px",alignItems:"center"}}>
                            <span style={{fontSize:12,color:D.gray600,fontWeight:500,flex:1}}>Delete this listing?</span>
                            <button onClick={()=>setDeletingId(null)}
                              style={{padding:"7px 14px",borderRadius:D.radiusSm,border:`1px solid ${D.gray200}`,background:D.white,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                              Cancel
                            </button>
                            <button onClick={()=>handleDeleteListing(l.id)}
                              style={{padding:"7px 14px",borderRadius:D.radiusSm,border:"none",background:D.red,color:D.white,fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* select-mode bulk action bar */}
                  {selectMode && (
                    <div style={{padding:"14px 16px",display:"flex",gap:8}}>
                      <button onClick={()=>setSelectedIds(new Set(myListings.map(l=>l.id)))}
                        style={{flex:1,padding:"12px 0",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`,background:D.white,color:D.gray800,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                        Select All
                      </button>
                      <button disabled={selectedIds.size===0} onClick={handleDeleteSelected}
                        style={{flex:2,padding:"12px 0",borderRadius:D.radius,border:"none",background:selectedIds.size>0?D.red:D.gray200,color:selectedIds.size>0?D.white:D.gray400,fontSize:13,fontWeight:800,cursor:selectedIds.size>0?"pointer":"default",fontFamily:"Noto Sans,sans-serif"}}>
                        Delete {selectedIds.size>0?`(${selectedIds.size})`:""}
                      </button>
                    </div>
                  )}

                  {!selectMode && (
                    <div style={{padding:"14px 16px"}}>
                      <button className="tap" onClick={()=>{setUserProfile(false);setTab("seller");}}
                        style={{width:"100%",padding:"13px",borderRadius:D.radius,border:`1.5px solid ${D.black}`,background:D.white,color:D.black,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                        + Post Another Listing
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Log out */}
          <div style={{padding:"16px",marginTop:8}}>
            <button className="tap" onClick={()=>signOut().catch(()=>{}).finally(()=>onSignOut?.())}
              style={{width:"100%",padding:"13px",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`,background:D.white,color:D.gray600,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
              Log Out
            </button>
          </div>
        </div>
      )}

      {/* ══ SELL TAB ═════════════════════════ */}
      {tab==="seller" && !sellerView && !userProfile && (
        <div className="fade" style={{paddingBottom:90,background:D.gray50}}>

          {/* ── STEP: type ── */}
          {sellStep==="type" && (
            <>
              <AppHeader title="List"/>
              {mySellerProfile===null && (
                <div style={{padding:"60px 24px",textAlign:"center",color:D.gray400}}>
                  <div style={{fontSize:13,fontWeight:600}}>Loading…</div>
                </div>
              )}
              {mySellerProfile!==null && (
                <div style={{padding:"16px",display:"flex",flexDirection:"column",gap:12,paddingBottom:100}}>

                  {/* ── Type cards ── */}
                  {[
                    {side:"goods",   icon:"📦", label:"Goods",    desc:"Physical products, clothing, food, art, and more",   agreed:goodsAgreed},
                    {side:"services",icon:"🛠️", label:"Services", desc:"Bookable skills, lessons, appointments, and errands", agreed:servicesAgreed},
                  ].map(({side,icon,label,desc,agreed}) => (
                    <div key={side} style={{background:D.white,border:`1.5px solid ${agreed?D.black:D.gray200}`,borderRadius:D.radius,padding:"16px",display:"flex",alignItems:"center",gap:14}}>
                      <div style={{width:44,height:44,background:agreed?D.black:D.gray100,borderRadius:D.radius,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{icon}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:15,fontWeight:900,letterSpacing:"-0.02em",marginBottom:2}}>{label}</div>
                        <div style={{fontSize:11,color:D.gray600,fontWeight:500}}>{desc}</div>
                      </div>
                      <button disabled={!agreed} onClick={()=>handleTypePick(side==="goods"?"goods":"service")}
                        style={{padding:"10px 14px",borderRadius:D.radius,border:"none",background:agreed?D.black:D.gray100,color:agreed?D.white:D.gray400,fontSize:12,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:agreed?"pointer":"default",whiteSpace:"nowrap",flexShrink:0}}>
                        {agreed?"List →":"Locked"}
                      </button>
                    </div>
                  ))}

                  {/* ── Goods terms ── */}
                  <div style={{background:D.white,border:`1px solid ${D.gray200}`,borderRadius:D.radius,overflow:"hidden",marginTop:8}}>
                    <div style={{padding:"12px 16px",borderBottom:`1px solid ${D.gray100}`}}>
                      <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,marginBottom:8}}>GOODS SELLER AGREEMENT</div>
                      <div style={{fontSize:10,color:D.gray600,fontWeight:500,lineHeight:1.75,whiteSpace:"pre-line"}}>
                        {`ACCURACY — You will accurately represent your items, including condition, authenticity, and description. You are responsible for fulfilling orders and resolving disputes with buyers.\n\nTAXES — You are responsible for collecting and remitting Pennsylvania sales tax (6%) on taxable sales. If your gross sales exceed $600 in a calendar year, nearme. will issue a Form 1099-K as required by federal law.\n\nPROHIBITED ITEMS — You will not list counterfeit, stolen, illegal, hazardous, or age-restricted goods, or any item prohibited by local, state, or federal law.\n\nCOTTAGE FOOD — Homemade food items must comply with Pennsylvania's Cottage Food Act. Only shelf-stable items may be sold. Meat, poultry, dairy, and refrigerated products require a PA Dept. of Agriculture food facility license.\n\nPLATFORM ROLE — nearme. is a marketplace platform and is not a party to your transactions.\n\nREFUNDS — You agree to honor your stated refund and return policy for each listing.\n\nACCOUNT — nearme. reserves the right to remove listings or suspend accounts that violate these terms.`}
                      </div>
                    </div>
                    <div style={{padding:"12px 16px 14px"}}>
                      <button
                        onClick={()=>{ if(agreeLoading===null) goodsAgreed ? handleRevoke("goods") : handleAgree("goods"); }}
                        style={{width:"100%",padding:"14px",borderRadius:D.radius,border:"none",background:goodsAgreed?D.gray100:D.black,color:goodsAgreed?D.gray600:D.white,fontSize:13,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                        <div style={{width:18,height:18,borderRadius:3,border:`2px solid ${goodsAgreed?"#999":D.white}`,background:goodsAgreed?D.white:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {agreeLoading==="goods" ? <span style={{fontSize:9}}>…</span> : goodsAgreed && <span style={{fontSize:11,fontWeight:900,color:D.black,lineHeight:1}}>✓</span>}
                        </div>
                        {agreeLoading==="goods" ? "Saving…" : goodsAgreed ? "Agreed to Goods Terms" : "I Agree to Goods Seller Terms"}
                      </button>
                    </div>
                  </div>

                  {/* ── Services terms ── */}
                  <div style={{background:D.white,border:`1px solid ${D.gray200}`,borderRadius:D.radius,overflow:"hidden"}}>
                    <div style={{padding:"12px 16px",borderBottom:`1px solid ${D.gray100}`}}>
                      <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:D.gray400,marginBottom:8}}>SERVICES SELLER AGREEMENT</div>
                      <div style={{fontSize:10,color:D.gray600,fontWeight:500,lineHeight:1.75,whiteSpace:"pre-line"}}>
                        {`INDEPENDENT CONTRACTOR — You are an independent contractor, not an employee or agent of nearme. You are solely responsible for your services, scheduling, pricing, and client relationships.\n\nQUALIFICATIONS — You are qualified and prepared to safely perform the services you list. You will not misrepresent your credentials, experience, or availability.\n\nLIABILITY — nearme. does not provide liability insurance. You are responsible for your own coverage.\n\nPA LICENSING — Cosmetology, nail, esthetics, and barbering require a PA State Board license. Transportation requires a valid PA driver's license and vehicle insurance. Food services must comply with the PA Cottage Food Act or hold a food facility license.\n\nTAXES — You are responsible for all federal and state income taxes. If gross payments exceed $600/year, nearme. will issue a Form 1099-K.\n\nCANCELLATIONS — You agree to honor your stated cancellation policy. Repeated no-shows may result in account suspension.\n\nACCOUNT — nearme. reserves the right to suspend accounts for violations or fraudulent activity.`}
                      </div>
                    </div>
                    <div style={{padding:"12px 16px 14px"}}>
                      <button
                        onClick={()=>{ if(agreeLoading===null) servicesAgreed ? handleRevoke("services") : handleAgree("services"); }}
                        style={{width:"100%",padding:"14px",borderRadius:D.radius,border:"none",background:servicesAgreed?D.gray100:D.black,color:servicesAgreed?D.gray600:D.white,fontSize:13,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                        <div style={{width:18,height:18,borderRadius:3,border:`2px solid ${servicesAgreed?"#999":D.white}`,background:servicesAgreed?D.white:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {agreeLoading==="services" ? <span style={{fontSize:9}}>…</span> : servicesAgreed && <span style={{fontSize:11,fontWeight:900,color:D.black,lineHeight:1}}>✓</span>}
                        </div>
                        {agreeLoading==="services" ? "Saving…" : servicesAgreed ? "Agreed to Services Terms" : "I Agree to Services Seller Terms"}
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </>
          )}

          {/* ── STEP: form ── */}
          {sellStep==="form" && (
            <>
              <AppHeader title="New Listing"/>
              <div style={{padding:"16px",paddingBottom:120}}>

                {/* Photos */}
                <div style={{marginBottom:20}}>
                  <Label style={{marginBottom:8}}>Photos <span style={{fontWeight:400,textTransform:"none",fontSize:10,color:D.gray400}}>(up to 5, optional)</span></Label>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:6}}>
                    {[0,1,2,3,4].map(i => {
                      if (listingPhotoURLs[i]) return (
                        <div key={i} style={{aspectRatio:"1/1",borderRadius:D.radius,overflow:"hidden",position:"relative",border:`1px solid ${D.gray200}`}}>
                          <img src={listingPhotoURLs[i]} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                          <button onClick={()=>removePhoto(i)} style={{position:"absolute",top:4,right:4,width:22,height:22,borderRadius:"50%",background:"rgba(0,0,0,0.65)",border:"none",color:D.white,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Noto Sans,sans-serif"}}>✕</button>
                          {i>0 && <button onClick={()=>movePhoto(i,-1)} style={{position:"absolute",bottom:4,left:4,width:20,height:20,borderRadius:4,background:"rgba(0,0,0,0.65)",border:"none",color:D.white,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Noto Sans,sans-serif",lineHeight:1}}>‹</button>}
                          {i<listingPhotoURLs.length-1 && <button onClick={()=>movePhoto(i,1)} style={{position:"absolute",bottom:4,right:4,width:20,height:20,borderRadius:4,background:"rgba(0,0,0,0.65)",border:"none",color:D.white,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Noto Sans,sans-serif",lineHeight:1}}>›</button>}
                        </div>
                      );
                      if (i===listingPhotoURLs.length) return (
                        <div key={i} className="tap" onClick={()=>photoInputRef.current.click()}
                          style={{aspectRatio:"1/1",borderRadius:D.radius,border:`1.5px dashed ${D.gray200}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",background:D.gray50}}>
                          <span style={{fontSize:24,color:D.gray400}}>+</span>
                        </div>
                      );
                      return <div key={i} style={{aspectRatio:"1/1",borderRadius:D.radius,border:`1.5px dashed ${D.gray100}`,background:D.gray50}}/>;
                    })}
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={handlePhotoAdd}/>
                </div>

                {/* Name */}
                <div style={{marginBottom:14}}>
                  <Label style={{marginBottom:6}}>Listing name</Label>
                  <input value={listingName} onChange={e=>setListingName(e.target.value)}
                    placeholder={sellKind==="goods"?"e.g. Vintage Levi's 501":"e.g. Acrylic Full Set"}
                    style={{width:"100%",padding:"12px 14px",fontSize:14,fontWeight:600,fontFamily:"Noto Sans,sans-serif",color:D.black,background:D.gray50,border:`1.5px solid ${D.gray200}`,borderRadius:D.radius}}/>
                </div>

                {/* Description */}
                <div style={{marginBottom:14}}>
                  <Label style={{marginBottom:6}}>Description <span style={{fontWeight:400,textTransform:"none",fontSize:10,color:D.gray400}}>optional</span></Label>
                  <textarea value={listingDesc} onChange={e=>setListingDesc(e.target.value)}
                    placeholder="Add details, what's included, conditions..."
                    rows={3}
                    style={{width:"100%",padding:"12px 14px",fontSize:13,fontWeight:500,fontFamily:"Noto Sans,sans-serif",color:D.black,background:D.gray50,border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,resize:"none"}}/>
                </div>

                {/* Price */}
                <div style={{marginBottom:14}}>
                  <Label style={{marginBottom:6}}>Price</Label>
                  <div style={{display:"flex",alignItems:"center",border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,background:D.gray50,overflow:"hidden"}}>
                    <span style={{padding:"12px 12px 12px 14px",fontSize:14,fontWeight:700,color:D.gray600}}>$</span>
                    <input type="number" min="0" step="0.01" value={listingPrice} onChange={e=>setListingPrice(e.target.value)}
                      placeholder="0.00"
                      style={{flex:1,padding:"12px 14px 12px 0",fontSize:14,fontWeight:700,fontFamily:"Noto Sans,sans-serif",color:D.black,background:"none",border:"none"}}/>
                  </div>
                </div>

                {/* Availability — services only */}
                {sellKind==="service" && (
                  <div style={{marginBottom:14}}>
                    <Label style={{marginBottom:8}}>Next available</Label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {[[0,"Now"],[30,"30 min"],[60,"1 hr"],[120,"2 hr"],[240,"4 hr"],[1440,"Tomorrow"]].map(([val,label]) => (
                        <button key={val} onClick={()=>setListingSlot(val)}
                          style={{padding:"5px 12px",border:`1.5px solid ${listingSlot===val?D.black:D.gray200}`,borderRadius:D.radius,background:listingSlot===val?D.black:D.white,color:listingSlot===val?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category dropdown */}
                <div style={{marginBottom:14}}>
                  <Label style={{marginBottom:6}}>Category</Label>
                  <select value={listingCat} onChange={e=>handleCatSelect(e.target.value)}
                    style={{width:"100%",padding:"12px 14px",fontSize:14,fontWeight:600,fontFamily:"Noto Sans,sans-serif",color:listingCat?D.black:D.gray400,background:D.gray50,border:`1.5px solid ${listingCat?D.black:D.gray200}`,borderRadius:D.radius,appearance:"none",WebkitAppearance:"none",backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",backgroundRepeat:"no-repeat",backgroundPosition:"right 14px center"}}>
                    <option value="">Select a category</option>
                    {CORE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Message to Book toggle — services only */}
                {sellKind==="service" && (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px",border:`1px solid ${listingMsgToBook?D.black:D.gray200}`,borderRadius:D.radius,marginBottom:10,background:listingMsgToBook?D.gray50:D.white}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:800}}>💬 Message to Book</div>
                      <div style={{fontSize:11,color:D.gray600,marginTop:2}}>Clients message you to arrange instead of booking directly</div>
                    </div>
                    <div onClick={()=>setListingMsgToBook(f=>!f)}
                      style={{width:44,height:24,borderRadius:12,background:listingMsgToBook?D.black:D.gray200,position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                      <div style={{position:"absolute",top:3,left:listingMsgToBook?23:3,width:18,height:18,borderRadius:"50%",background:D.white,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                    </div>
                  </div>
                )}

                {/* Flash toggle */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px",border:`1px solid ${listingFlash?D.red:D.gray200}`,borderRadius:D.radius,marginBottom:20,background:listingFlash?"#FFF0F0":D.white}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:listingFlash?D.red:D.black}}>⚡ Flash listing</div>
                    <div style={{fontSize:11,color:D.gray600,marginTop:2}}>Alerts followers · appears at top of feed</div>
                  </div>
                  <div onClick={()=>setListingFlash(f=>!f)}
                    style={{width:44,height:24,borderRadius:12,background:listingFlash?D.red:D.gray200,position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:3,left:listingFlash?23:3,width:18,height:18,borderRadius:"50%",background:D.white,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                  </div>
                </div>

                {sellError && <div style={{fontSize:12,color:D.red,fontWeight:600,marginBottom:12,padding:"10px 12px",background:"#FFF0F0",borderRadius:D.radiusSm,border:`1px solid #FFCCCC`}}>{sellError}</div>}

                <button className="tap" onClick={handleSellSubmit}
                  disabled={sellLoading||!listingName.trim()||!listingPrice||!listingCat}
                  style={{width:"100%",padding:"16px",borderRadius:D.radius,border:"none",background:listingName.trim()&&listingPrice&&listingCat?D.black:D.gray200,color:listingName.trim()&&listingPrice&&listingCat?D.white:D.gray400,fontSize:14,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:listingName.trim()&&listingPrice&&listingCat?"pointer":"default"}}>
                  {sellLoading?"Posting…":"Post Listing →"}
                </button>

                <div className="tap" onClick={()=>setSellStep("type")} style={{marginTop:16,textAlign:"center",fontSize:12,color:D.gray400,fontWeight:600,cursor:"pointer",textDecoration:"underline"}}>← Back</div>
              </div>
            </>
          )}

          {/* ── STEP: disclosure ── */}
          {sellStep==="disclosure" && pendingDisclosure && (() => {
            const allChecked = pendingDisclosure.checks.every(c => disclosureChecked.has(c.id));
            return (
              <>
                <AppHeader title="Listing Requirement"/>
                <div style={{padding:"24px 24px 0",fontFamily:"'DM Sans',sans-serif"}}>
                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:18,fontWeight:900,letterSpacing:"-0.03em",marginBottom:8}}>{pendingDisclosure.title}</div>
                    <div style={{fontSize:13,color:D.gray600,fontWeight:500,lineHeight:1.5}}>{pendingDisclosure.intro}</div>
                  </div>
                  {pendingDisclosure.checks.map(c => {
                    const checked = disclosureChecked.has(c.id);
                    return (
                      <div key={c.id} onClick={() => setDisclosureChecked(prev => { const n=new Set(prev); n.has(c.id)?n.delete(c.id):n.add(c.id); return n; })}
                        style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 0",borderBottom:`1px solid ${D.gray100}`,cursor:"pointer"}}>
                        <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${checked?D.black:D.gray200}`,background:checked?D.black:D.white,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",marginTop:1}}>
                          {checked && <span style={{color:D.white,fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
                        </div>
                        <span style={{fontSize:12,fontWeight:500,color:D.gray800,lineHeight:1.55}}>{c.label}</span>
                      </div>
                    );
                  })}
                  <div style={{marginTop:20,padding:"12px",background:D.gray50,borderRadius:D.radius,border:`1px solid ${D.gray200}`}}>
                    <span style={{fontSize:11,color:D.gray600,fontWeight:500,lineHeight:1.5}}>⚖️ These disclosures are specific to <strong>{pendingDisclosure.cat}</strong> listings and are required by Pennsylvania law. You only need to confirm this once.</span>
                  </div>
                </div>
                <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,padding:"16px 24px 36px",borderTop:`1px solid ${D.gray100}`,background:D.white,zIndex:10}}>
                  <button disabled={!allChecked}
                    onClick={() => { saveDisc(sellUserId, pendingDisclosure.cat); setPendingDisclosure(null); setDisclosureChecked(new Set()); setSellStep("form"); }}
                    style={{width:"100%",padding:"15px",borderRadius:D.radius,border:"none",background:allChecked?D.black:D.gray200,color:allChecked?D.white:D.gray400,fontSize:14,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:allChecked?"pointer":"default"}}>
                    Confirm & Continue →
                  </button>
                  <div onClick={() => { setListingCat(""); setPendingDisclosure(null); setDisclosureChecked(new Set()); setSellStep("form"); }}
                    style={{textAlign:"center",marginTop:12,fontSize:12,color:D.gray400,fontWeight:600,cursor:"pointer",textDecoration:"underline"}}>
                    ← Choose a different category
                  </div>
                </div>
              </>
            );
          })()}

          {/* ── STEP: done ── */}
          {sellStep==="done" && (
            <>
              <AppHeader title="List"/>
              <div style={{padding:"60px 24px",textAlign:"center"}}>
                <div style={{fontSize:52,marginBottom:16}}>✅</div>
                <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em",marginBottom:8}}>Listing posted!</div>
                <div style={{fontSize:13,color:D.gray600,marginBottom:36,lineHeight:1.5}}>Your listing is live on nearme.</div>
                <button className="tap" onClick={resetSellFlow}
                  style={{width:"100%",padding:"14px",borderRadius:D.radius,border:`1.5px solid ${D.black}`,background:D.white,color:D.black,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif",marginBottom:10}}>
                  Post Another
                </button>
                <button className="tap" onClick={()=>{resetSellFlow();setTab("explore");}}
                  style={{width:"100%",padding:"14px",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                  Back to Explore
                </button>
              </div>
            </>
          )}

        </div>
      )}

      {/* ══ MESSAGE COMPOSE SHEET ═══════════ */}
      {showMsgCompose && selected && (
        <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
          <div onClick={()=>setShowMsgCompose(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)"}}/>
          <div className="sheet" style={{position:"relative",background:D.white,borderRadius:"18px 18px 0 0",padding:"20px 16px 40px",maxWidth:430,width:"100%",margin:"0 auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.03em"}}>Message {selected.seller.handle}</div>
              <button onClick={()=>setShowMsgCompose(false)} style={{border:"none",background:"none",fontSize:18,cursor:"pointer",color:D.gray600,lineHeight:1}}>✕</button>
            </div>
            {msgSent ? (
              <div style={{textAlign:"center",padding:"24px 0"}}>
                <div style={{fontSize:28,marginBottom:10}}>✅</div>
                <div style={{fontSize:15,fontWeight:800,marginBottom:6}}>Message sent!</div>
                <div style={{fontSize:13,color:D.gray500}}>They'll see it in their notifications.</div>
              </div>
            ) : (
              <>
                <textarea
                  value={msgText}
                  onChange={e=>setMsgText(e.target.value)}
                  placeholder={`Ask about ${selected.name}…`}
                  rows={4}
                  style={{width:"100%",border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,padding:"12px",fontSize:14,resize:"none",fontFamily:"Noto Sans,sans-serif",color:D.black,outline:"none",boxSizing:"border-box"}}
                />
                <button
                  className="tap"
                  disabled={!msgText.trim() || msgSending || !selected.seller.userId}
                  onClick={async()=>{
                    if(!msgText.trim()||!selected.seller.userId)return;
                    setMsgSending(true);
                    try{ await sendMessage({receiver_id:selected.seller.userId,body:msgText.trim()}); setMsgSent(true); }
                    catch(e){ console.error(e); }
                    setMsgSending(false);
                  }}
                  style={{width:"100%",marginTop:10,padding:"14px 0",borderRadius:D.radius,border:"none",background:msgText.trim()&&selected.seller.userId?D.black:D.gray200,color:msgText.trim()&&selected.seller.userId?D.white:D.gray400,fontSize:14,fontWeight:900,fontFamily:"Noto Sans,sans-serif",cursor:msgText.trim()?"pointer":"default",letterSpacing:"0.02em"}}>
                  {msgSending?"Sending…":"Send Message"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ NOTIFICATIONS OVERLAY ═══════════ */}
      {showNotifs && (
        <div style={{position:"fixed",inset:0,zIndex:200,background:D.white,overflowY:"auto",display:"flex",flexDirection:"column",maxWidth:430,margin:"0 auto"}}>
          <div style={{position:"sticky",top:0,zIndex:10,background:D.white,borderBottom:`1px solid ${D.gray100}`}}>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px 0"}}>
              <div className="tap" onClick={()=>setShowNotifs(false)} style={{fontSize:20,lineHeight:1,color:D.black}}>←</div>
              <div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.03em"}}>Inbox</div>
            </div>
            <div style={{display:"flex",padding:"0 16px"}}>
              {[["notifications","Activity"],["messages","Messages"]].map(([k,label])=>(
                <div key={k} onClick={()=>setNotifsTab(k)} style={{padding:"10px 0",marginRight:24,fontSize:13,fontWeight:800,letterSpacing:"-0.01em",borderBottom:notifsTab===k?`2.5px solid ${D.black}`:"2.5px solid transparent",color:notifsTab===k?D.black:D.gray400,cursor:"pointer"}}>
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Activity tab */}
          {notifsTab==="notifications" && (<>
            {notifsLoading && <div style={{padding:40,textAlign:"center",color:D.gray400,fontSize:13}}>Loading…</div>}
            {!notifsLoading && notifs.filter(n=>n.type!=="message").length === 0 && (
              <div style={{padding:"60px 24px",textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:12}}>🔔</div>
                <div style={{fontSize:15,fontWeight:800,color:D.gray800,marginBottom:6}}>No activity yet</div>
                <div style={{fontSize:13,color:D.gray500}}>You'll see bookings, follows, and reviews here.</div>
              </div>
            )}
            {!notifsLoading && notifs.filter(n=>n.type!=="message").map(n => {
              const icons = { booking:"📅", follow:"👥", review:"⭐", confirmed:"✅" };
              const diff = (Date.now() - new Date(n.at)) / 1000;
              const ago = diff < 60 ? "just now" : diff < 3600 ? `${Math.floor(diff/60)}m ago` : diff < 86400 ? `${Math.floor(diff/3600)}h ago` : `${Math.floor(diff/86400)}d ago`;
              return (
                <div key={n.id} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"14px 16px",borderBottom:`1px solid ${D.gray100}`}}>
                  <div style={{width:40,height:40,borderRadius:"50%",background:D.gray100,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                    {n.avatarUrl ? <img src={n.avatarUrl} alt="" style={{width:40,height:40,borderRadius:"50%",objectFit:"cover"}}/> : icons[n.type]}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:D.black,lineHeight:1.4}}>{n.text}</div>
                    {n.sub && <div style={{fontSize:12,color:D.gray500,marginTop:2,lineHeight:1.4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.sub}</div>}
                    <div style={{fontSize:11,color:D.gray400,marginTop:4}}>{ago}</div>
                  </div>
                </div>
              );
            })}
          </>)}

          {/* Messages tab */}
          {notifsTab==="messages" && (<>
            {notifsLoading && <div style={{padding:40,textAlign:"center",color:D.gray400,fontSize:13}}>Loading…</div>}
            {!notifsLoading && notifs.filter(n=>n.type==="message").length === 0 && (
              <div style={{padding:"60px 24px",textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:12}}>💬</div>
                <div style={{fontSize:15,fontWeight:800,color:D.gray800,marginBottom:6}}>No messages yet</div>
                <div style={{fontSize:13,color:D.gray500}}>When sellers or buyers message you, they'll appear here.</div>
              </div>
            )}
            {!notifsLoading && notifs.filter(n=>n.type==="message").map(n => {
              const diff = (Date.now() - new Date(n.at)) / 1000;
              const ago = diff < 60 ? "just now" : diff < 3600 ? `${Math.floor(diff/60)}m ago` : diff < 86400 ? `${Math.floor(diff/3600)}h ago` : `${Math.floor(diff/86400)}d ago`;
              return (
                <div key={n.id} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderBottom:`1px solid ${D.gray100}`}}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:D.gray100,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,flexShrink:0,overflow:"hidden"}}>
                    {n.avatarUrl ? <img src={n.avatarUrl} alt="" style={{width:44,height:44,objectFit:"cover"}}/> : n.initials}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
                      <span style={{fontSize:14,fontWeight:800,color:D.black}}>{n.text.replace(" sent you a message","")}</span>
                      <span style={{fontSize:11,color:D.gray400,flexShrink:0,marginLeft:8}}>{ago}</span>
                    </div>
                    <div style={{fontSize:13,color:D.gray500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.sub}</div>
                  </div>
                </div>
              );
            })}
          </>)}
          <div style={{height:40}}/>
        </div>
      )}

      {/* ══ BOTTOM NAV ═══════════════════════ */}
      {!userProfile && !sellerView && !isDesktop && (
        <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:"rgba(255,255,255,0.97)",backdropFilter:"blur(8px)",borderTop:`1px solid ${D.gray200}`,display:"flex",zIndex:50}}>
          {[{key:"feed",icon:"⊡",label:"Feed"},{key:"explore",icon:"◎",label:"Explore"},{key:"seller",special:true},{key:"notifs",label:"Inbox"},{key:"profile",icon:"○",label:"Profile"}].map(n => (
            <div key={n.key} className="tap" onClick={()=>{
              if(n.key==="profile") return requireAuth(()=>setUserProfile(true));
              if(n.key==="seller") return requireAuth(()=>setTab("seller"));
              if(n.key==="feed") return requireAuth(()=>setTab("feed"));
              if(n.key==="notifs") return requireAuth(()=>{setShowNotifs(true);loadNotifs();setNotifsTab("notifications");localStorage.setItem('nearme_notifs_seen',new Date().toISOString());setHasUnread(false);});
              setTab(n.key);
            }} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:n.special?"8px 0":"10px 0 14px",cursor:"pointer",background:n.special?D.black:D.white,borderRight:n.key!=="profile"?`1px solid ${D.gray100}`:"none"}}>
              {n.special
                ? <span style={{fontSize:22,fontWeight:900,color:D.white,lineHeight:1}}>+</span>
                : <>
                    {n.key==="notifs"
                      ? <div style={{position:"relative",marginBottom:2}}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showNotifs?D.black:D.gray400} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                          {hasUnread && <div style={{position:"absolute",top:-2,left:-2,width:7,height:7,borderRadius:"50%",background:D.red,border:`1.5px solid ${D.white}`}}/>}
                        </div>
                      : <span style={{fontSize:19,lineHeight:1,marginBottom:2,color:tab===n.key?D.black:D.gray300}}>{n.icon}</span>}
                    <span style={{fontSize:9,fontWeight:700,letterSpacing:"0.05em",color:(n.key==="notifs"?showNotifs:tab===n.key)?D.black:D.gray400}}>{n.label}</span>
                    {(n.key==="notifs"?showNotifs:tab===n.key) && <div style={{width:14,height:2,background:D.red,borderRadius:1,marginTop:3}}/>}
                  </>}
            </div>
          ))}
        </div>
      )}

      {/* ══ LISTING PAGE ══════════════════════ */}
      {selected && (() => {
        const imgs = selected.image_urls?.length ? selected.image_urls : selected.seller?.posts?.[0]?.img ? [selected.seller.posts[0].img] : [];
        const ImageGallery = ({thumb}) => (
          <div style={{position:"relative",background:D.gray50}}>
            {thumb && imgs.length > 1 && (
              <div style={{display:"flex",flexDirection:"column",gap:6,padding:"8px 0 8px 8px",position:"absolute",top:0,left:0,zIndex:2,maxHeight:"100%",overflowY:"auto"}}>
                {imgs.map((url,i) => (
                  <div key={i} style={{width:52,height:52,border:`2px solid ${D.gray200}`,borderRadius:6,overflow:"hidden",cursor:"pointer",background:D.white,flexShrink:0}}>
                    <img src={url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  </div>
                ))}
              </div>
            )}
            <div style={{aspectRatio:"1/1",overflowX:"auto",display:"flex",scrollSnapType:"x mandatory",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",marginLeft: thumb && imgs.length>1 ? 68 : 0}}>
              {imgs.length > 0
                ? imgs.map((url,i) => <img key={i} src={url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",flexShrink:0,scrollSnapAlign:"start",display:"block"}}/>)
                : <div style={{width:"100%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:72,color:D.gray300}}>{isProd(selected)?"📦":"🛠️"}</div>}
            </div>
            {imgs.length > 1 && !thumb && (
              <div style={{display:"flex",gap:5,justifyContent:"center",padding:"10px 0"}}>
                {imgs.map((_,i) => <div key={i} style={{width:6,height:6,borderRadius:"50%",background:D.gray400}}/>)}
              </div>
            )}
            {selected.flash && <div style={{position:"absolute",top:12,left:12,background:D.red,borderRadius:D.radiusSm,padding:"4px 10px",display:"flex",alignItems:"center",gap:5}}><div style={{width:5,height:5,borderRadius:"50%",background:D.white,animation:"fp 1.4s ease-in-out infinite"}}/><span style={{fontSize:10,fontWeight:800,color:D.white,letterSpacing:"0.06em"}}>FLASH</span></div>}
          </div>
        );
        const BuyBox = ({border}) => (
          <div style={{border: border ? `1px solid ${D.gray200}` : "none",borderRadius: border ? D.radius : 0,padding: border ? "20px 16px" : "0"}}>
            <div style={{fontSize:28,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>${selected.price}</div>
            {!isProd(selected) && <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}><div style={{width:8,height:8,borderRadius:"50%",background:slotColor(selected.slot||0)}}/><span style={{fontSize:13,fontWeight:700,color:slotColor(selected.slot||0)}}>{slotLabel(selected.slot||0)}</span></div>}
            {isProd(selected) && (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:13,color:D.gray700,marginBottom:8}}><span style={{fontWeight:700}}>In Stock</span></div>
                <div style={{display:"flex",gap:6}}>
                  {[["pickup","📍 Pickup"],["delivery","🛵 Delivery"],["mail","📬 Mail"]].map(([k,label]) => (
                    <button key={k} onClick={()=>setGoodsFulfill(k)} style={{flex:1,padding:"7px 0",border:`1.5px solid ${goodsFulfill===k?D.black:D.gray200}`,borderRadius:D.radiusSm,background:goodsFulfill===k?D.black:D.white,color:goodsFulfill===k?D.white:D.gray800,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            <div style={{fontSize:13,color:D.gray600,marginBottom:16}}>
              Sold by <span className="tap" onClick={()=>{closeListing();openSeller(selected.seller,"services");}} style={{fontWeight:700,color:D.black,cursor:"pointer",textDecoration:"underline"}}>{selected.seller?.handle || selected.seller?.name}</span>
              {selected.seller?.distance!=null && <span> · 📍 {selected.seller.distance}mi away</span>}
            </div>
            {selected.messageToBook
              ? <button className="tap" onClick={()=>requireAuth(()=>{setMsgSent(false);setMsgText("");setShowMsgCompose(true);})} style={{width:"100%",padding:"14px 0",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:15,fontWeight:900,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",letterSpacing:"0.02em",marginBottom:8}}>
                  💬 Message to Book
                </button>
              : <button disabled style={{width:"100%",padding:"14px 0",borderRadius:D.radius,border:"none",background:D.gray200,color:D.gray400,fontSize:15,fontWeight:900,fontFamily:"Noto Sans,sans-serif",cursor:"not-allowed",letterSpacing:"0.02em",marginBottom:8}}>
                  {isProd(selected)?"Buy Now":"Book Now"}
                </button>
            }
            {!selected.messageToBook && (
              <button className="tap" onClick={()=>requireAuth(()=>{setMsgSent(false);setMsgText("");setShowMsgCompose(true);})} style={{width:"100%",padding:"13px 0",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`,background:D.white,fontSize:14,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",color:D.black,letterSpacing:"0.01em"}}>
                💬 Message Seller
              </button>
            )}
          </div>
        );
        return (
          <div style={{position:"fixed",inset:0,zIndex:300,background:D.white,overflowY:"auto"}}>
            {/* top bar */}
            <div style={{position:"sticky",top:0,zIndex:10,background:D.white,borderBottom:`1px solid ${D.gray100}`,display:"flex",alignItems:"center",gap:12,padding:"13px 16px"}}>
              <button onClick={()=>closeListing()} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:D.black,padding:0,lineHeight:1,flexShrink:0}}>←</button>
              <div style={{flex:1,fontSize:13,fontWeight:700,color:D.gray600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selected.seller?.handle || ""}</div>
            </div>

            {(() => {
              const similar = allListings.filter(l => l.lid !== selected.lid && (l.kind === selected.kind || l.seller?.trade === selected.seller?.trade)).slice(0, 8);
              const SimilarSection = ({mobile}) => similar.length === 0 ? null : (
                <div style={{padding: mobile ? "24px 16px 40px" : "32px 32px 60px", maxWidth: mobile ? undefined : 1100, margin: mobile ? undefined : "0 auto"}}>
                  <div style={{borderTop:`1px solid ${D.gray100}`,paddingTop: mobile ? 20 : 28,marginBottom: mobile ? 16 : 20}}>
                    <div style={{fontSize: mobile ? 16 : 20,fontWeight:900,letterSpacing:"-0.02em",marginBottom: mobile ? 12 : 16}}>Similar listings</div>
                    {mobile
                      ? <div style={{display:"flex",gap:12,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
                          {similar.map(l => <div key={l.lid} style={{width:150,flexShrink:0}}><Card l={l}/></div>)}
                        </div>
                      : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:16}}>
                          {similar.map(l => <Card key={l.lid} l={l}/>)}
                        </div>
                    }
                  </div>
                </div>
              );
              if (isDesktop) return (
                <>
                  <div style={{maxWidth:1100,margin:"0 auto",padding:"32px 32px 0",display:"grid",gridTemplateColumns:"1fr 420px",gap:40,alignItems:"start"}}>
                    <ImageGallery thumb={true}/>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:D.gray400,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>{selected.kind==="product"?"Physical Goods":"Service"}</div>
                      <div style={{fontSize:26,fontWeight:900,letterSpacing:"-0.03em",lineHeight:1.25,marginBottom:10}}>{selected.name}</div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                        <span style={{fontSize:14,color:"#F0A500",letterSpacing:2}}>{"★".repeat(Math.round(selected.seller?.rating||0))+"☆".repeat(5-Math.round(selected.seller?.rating||0))}</span>
                        <span style={{fontSize:13,color:D.gray500}}>{selected.seller?.rating ? `${selected.seller.rating} · ${selected.seller.reviews||0} reviews` : "New seller"}</span>
                      </div>
                      {selected.description && (
                        <div style={{marginBottom:20}}>
                          <div style={{fontSize:18,fontWeight:800,letterSpacing:"-0.02em",marginBottom:10}}>About this listing</div>
                          <div style={{fontSize:14,color:D.gray700,lineHeight:1.7}}>{selected.description}</div>
                        </div>
                      )}
                      <div style={{borderTop:`1px solid ${D.gray100}`,paddingTop:16}}>
                        <BuyBox border={true}/>
                      </div>
                    </div>
                  </div>
                  <SimilarSection mobile={false}/>
                </>
              );
              return (
                <div style={{paddingBottom:40}}>
                  <ImageGallery thumb={false}/>
                  <div style={{padding:"16px 16px 0"}}>
                    <div style={{fontSize:11,fontWeight:700,color:D.gray400,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>{selected.kind==="product"?"Physical Goods":"Service"}</div>
                    <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.03em",lineHeight:1.3,marginBottom:8}}>{selected.name}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:16,paddingBottom:16,borderBottom:`1px solid ${D.gray100}`}}>
                      <span style={{fontSize:13,color:"#F0A500"}}>{"★".repeat(Math.round(selected.seller?.rating||0))}</span>
                      <span style={{fontSize:13,color:D.gray500}}>{selected.seller?.rating ? `${selected.seller.rating} (${selected.seller.reviews||0})` : "New seller"}</span>
                    </div>
                    <div style={{paddingBottom:16,borderBottom:`1px solid ${D.gray100}`,marginBottom:16}}>
                      <BuyBox border={false}/>
                    </div>
                    {selected.description && (
                      <div style={{marginBottom:20}}>
                        <div style={{fontSize:16,fontWeight:800,letterSpacing:"-0.02em",marginBottom:8}}>About this listing</div>
                        <div style={{fontSize:14,color:D.gray700,lineHeight:1.7}}>{selected.description}</div>
                      </div>
                    )}
                    <div className="tap" onClick={()=>{closeListing();openSeller(selected.seller);}} style={{display:"flex",alignItems:"center",gap:10,padding:"14px",border:`1px solid ${D.gray200}`,borderRadius:D.radius,cursor:"pointer",marginBottom:0}}>
                      <Avatar url={selected.seller?.avatarUrl} initials={selected.seller?.avatar} size={36} radius={99}/>
                      <div>
                        <div style={{fontSize:13,fontWeight:800}}>{selected.seller?.name}</div>
                        <div style={{fontSize:12,color:D.gray500}}>{selected.seller?.handle} · View store →</div>
                      </div>
                    </div>
                  </div>
                  <SimilarSection mobile={true}/>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ══ WELCOME MODAL ════════════════ */}
      {showWelcome && (
        <div style={{position:"fixed",inset:0,zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 20px",background:"rgba(0,0,0,0.6)"}}>
          <div style={{position:"relative",background:D.white,borderRadius:10,padding:"32px 24px",width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.25)",maxHeight:"90vh",overflowY:"auto",fontFamily:"Noto Sans,sans-serif"}}>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:14}}>
              Welcome to nearme<span style={{color:D.red}}>.</span>
            </div>
            <div style={{fontSize:14,lineHeight:1.6,color:D.gray700,marginBottom:12}}>
              nearme. is a platform for local businesses, artists, sellers, and more to list their products and services and find local clients.
            </div>
            <div style={{fontSize:14,lineHeight:1.6,color:D.gray700,marginBottom:12}}>
              We're currently in early development and test launching in <strong>West Chester, Pennsylvania</strong>. We're in a <strong>window shopping phase</strong>, feel free to list your services, browse sellers, and make connections as we work towards full transactions.
            </div>
            <div style={{fontSize:14,lineHeight:1.6,color:D.gray700,marginBottom:12}}>
              Have feedback, bugs, ideas, or thoughts? DM us at <strong>@nearme.spot</strong> on Instagram.
            </div>
            <div style={{fontSize:14,lineHeight:1.6,color:D.gray700,marginBottom:24}}>
              With West Chester as our launch site, you can help shape this to benefit yourself and your community. Thank you for your support and patience as we make everything perfect.
            </div>
            <button onClick={dismissWelcome} style={{width:"100%",padding:"14px 0",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:14,fontWeight:900,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",letterSpacing:"0.02em"}}>
              OK!
            </button>
          </div>
        </div>
      )}

      {/* ══ GUEST AUTH PROMPT ════════════════ */}
      {showAuthPrompt && (
        <div style={{position:"fixed",inset:0,zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 24px"}}>
          <div onClick={()=>setShowAuthPrompt(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)"}}/>
          <div style={{position:"relative",background:D.white,borderRadius:12,padding:"32px 24px",width:"100%",maxWidth:340,textAlign:"center",boxShadow:"0 16px 48px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:36,marginBottom:12}}>🔒</div>
            <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.03em",marginBottom:8}}>Sign in to continue</div>
            <div style={{fontSize:13,color:D.gray600,lineHeight:1.55,marginBottom:24}}>Create a free account or log in to save, follow, message sellers, and book.</div>
            <button onClick={()=>{setShowAuthPrompt(false);onSignIn?.();}} style={{width:"100%",padding:"14px",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:14,fontWeight:800,fontFamily:"Noto Sans,sans-serif",cursor:"pointer",marginBottom:10}}>
              Log in / Sign up
            </button>
            <button onClick={()=>setShowAuthPrompt(false)} style={{width:"100%",padding:"12px",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`,background:D.white,color:D.gray600,fontSize:13,fontWeight:700,fontFamily:"Noto Sans,sans-serif",cursor:"pointer"}}>
              Continue browsing
            </button>
          </div>
        </div>
      )}

      {/* ── RADIUS MAP MODAL ── */}
      {showRadiusModal && (() => {
        const WCU = { lat: 39.9607, lng: -75.6050 };
        const center = userCoords || WCU;
        const radiusM = sliderRadius * 1609.34;
        const label = sliderRadius < 1 ? `${(sliderRadius * 5280)|0} ft` : `${sliderRadius} mi`;
        return (
          <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
            <div onClick={()=>setShowRadiusModal(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.5)"}}/>
            <div style={{position:"relative",background:D.white,borderRadius:"18px 18px 0 0",overflow:"hidden",paddingBottom:32}}>
              {/* header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px 14px"}}>
                <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.04em"}}>Search radius</div>
                <button onClick={()=>setShowRadiusModal(false)} style={{border:"none",background:"none",fontSize:18,cursor:"pointer",color:D.gray600,lineHeight:1}}>✕</button>
              </div>

              {/* map */}
              <div style={{height:240,width:"100%"}}>
                <MapContainer
                  center={[center.lat, center.lng]}
                  zoom={14}
                  style={{height:"100%",width:"100%"}}
                  zoomControl={true}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                  />
                  <Circle
                    center={[center.lat, center.lng]}
                    radius={radiusM}
                    pathOptions={{color:"#FF0000",fillColor:"#FF0000",fillOpacity:0.12,weight:2}}
                  />
                  <MapFit center={center} radiusMi={sliderRadius} />
                </MapContainer>
              </div>

              {/* slider */}
              <div style={{padding:"20px 24px 0"}}>
                <div style={{textAlign:"center",fontSize:28,fontWeight:900,letterSpacing:"-0.04em",marginBottom:14}}>
                  {label}
                </div>
                <style>{`
                  .radius-slider { -webkit-appearance:none; width:100%; height:4px; border-radius:2px; background:linear-gradient(to right, #0A0A0A ${((sliderRadius-0.25)/9.75)*100}%, #DDDDDD ${((sliderRadius-0.25)/9.75)*100}%); outline:none; cursor:pointer; }
                  .radius-slider::-webkit-slider-thumb { -webkit-appearance:none; width:22px; height:22px; border-radius:50%; background:#0A0A0A; cursor:pointer; box-shadow:0 2px 6px rgba(0,0,0,0.25); }
                `}</style>
                <input
                  type="range" min={0.25} max={10} step={0.25}
                  value={sliderRadius}
                  onChange={e=>setSliderRadius(+e.target.value)}
                  className="radius-slider"
                />
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:D.gray400,fontWeight:600,marginTop:4}}>
                  <span>0.25 mi</span><span>10 mi</span>
                </div>

                <div style={{display:"flex",gap:10,marginTop:20}}>
                  <button onClick={()=>{setRadiusFilter(null);setShowRadiusModal(false);}}
                    style={{flex:1,padding:"12px 0",borderRadius:D.radius,border:`1.5px solid ${D.gray200}`,background:D.white,color:D.gray800,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                    Any distance
                  </button>
                  <button onClick={()=>{setRadiusFilter(sliderRadius);setShowRadiusModal(false);}}
                    style={{flex:2,padding:"12px 0",borderRadius:D.radius,border:"none",background:D.black,color:D.white,fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"Noto Sans,sans-serif"}}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
